import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBranch } from "../context/BranchContext";
import { getStoredUser, isPremiumUser } from "../controllers/authController";
import {
  FOCUS_LOSS_PARAMS,
  FOCUS_RATED_POWER,
  matchesFocusTarget,
} from "../utils/nodeSettingsLink";
import {
  updatePassword,
  updateEmail,
  updatePhone,
  updateConsumerInfo,
  updateOfflineMeterEmail,
  getConsumerInfo,
  deleteOfflineMeterEmail,
  updateUnitPrices,
} from "../controllers/userController";
import {
  getEmails,
  addEmail,
  deleteEmail,
  updateEmail as updateEmailAddress,
  getDailyReportSchedule,
  saveDailyReportSchedule,
  getEnergyEfficiencySchedule,
  saveEnergyEfficiencySchedule,
  getEnvironmentReportSchedule,
  saveEnvironmentReportSchedule,
} from "../controllers/emailController";
import EnvironmentScheduleEditor from "../components/EnvironmentScheduleEditor";
import {
  getClientMachines,
  updateMachineConfig,
  getClientProfileSettings,
  updateClientProfileSettings,
  getClientEmails,
  getMachineAlertEmails,
  setMachineAlertEmails,
} from "../controllers/machineController";
import NestedMachineSelector from "../components/NestedMachineSelector";
import FieldHelp from "../help/FieldHelp";
import ExcludeNodeTree from "../components/ExcludeNodeTree";
import BranchScheduleEditor from "../components/BranchScheduleEditor";
import api, { getApiBase } from "../utils/api";
import { buildMachineColorMap } from "../utils/machineColors";
import "./InfoTab.css";

/** How long the deep-linked field stays highlighted after landing. */
const FOCUS_FLASH_MS = 2200;

const METER_CACHE_PREFIX = "enviz-meter-types:";

const readCachedMeterTypes = (username) => {
  if (!username) return null;
  try {
    const raw = localStorage.getItem(METER_CACHE_PREFIX + username);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const writeCachedMeterTypes = (username, types) => {
  if (!username) return;
  try {
    if (types) {
      localStorage.setItem(METER_CACHE_PREFIX + username, JSON.stringify(types));
    } else {
      localStorage.removeItem(METER_CACHE_PREFIX + username);
    }
  } catch (e) { }
};

const sections = [
  {
    id: "personal",
    title: "Details",
    subtitle: "Update password, email, and phone",
  },
  {
    id: "machines",
    title: "Node Settings",
    subtitle: "Manage node configurations",
  },
  {
    id: "notifications",
    title: "Notifications",
    subtitle: "Update the notification settings",
  },
  {
    id: "multiTier",
    title: "Multi Tier",
    subtitle: "Add credentials for machines",
  },
  {
    id: "dataApi",
    title: "Data API",
    subtitle: "Download the integration manual",
  },
];

// Built by enviz_api/scripts/build_data_api_manual.py into public/docs/, so CRA
// serves it from the site root in both `npm start` and a production build.
const DATA_API_MANUAL_PATH = "/docs/EnViz-Data-API-Manual.pdf";

// Public origin clients call. Deliberately NOT getApiBase(): that follows
// whatever host the dashboard itself is served from, which in development is
// localhost -- useless to a client copying the example. Keep in step with
// BASE_URL in enviz_api/scripts/build_data_api_manual.py.
const DATA_API_BASE =
  process.env.REACT_APP_DATA_API_BASE || "https://testenfo.com/api";

// Convert "hh AM/PM" (e.g. "09 AM") to "HH:mm" (24h) for <input type="time">.
// Passes through values that are already "HH:mm".
const formatTimeForInput = (timeStr) => {
  if (!timeStr) return "";
  const match = String(timeStr).match(/(\d+)\s*(AM|PM)/i);
  if (!match) return timeStr;
  let [, h, ampm] = match;
  h = parseInt(h, 10);
  if (ampm.toUpperCase() === "PM" && h < 12) h += 12;
  if (ampm.toUpperCase() === "AM" && h === 12) h = 0;
  return `${h.toString().padStart(2, "0")}:00`;
};

// Normalize a schedule from the backend into the grouped editor shape:
//   [{ branch, emails, days, windows: [{ start, end, last_sent }] }]
// Accepts the grouped shape directly, and upgrades a legacy flat entry
// ({ start, end, emails }) into a single-window group.
const normalizeSchedule = (schedule) =>
  (Array.isArray(schedule) ? schedule : []).map((g) => {
    const rawDays = Array.isArray(g.days)
      ? g.days
      : g.days !== undefined && g.days !== null
        ? [g.days]
        : [1];
    const parsedDays = rawDays.map(Number).filter((d) => !isNaN(d) && d > 0).sort((a, b) => a - b);
    const finalDays = parsedDays.length > 0 ? parsedDays : [1];

    if (Array.isArray(g.windows)) {
      return {
        branch: g.branch || "ALL",
        emails: Array.isArray(g.emails) ? g.emails : [],
        days: finalDays,
        weekly_day: g.weekly_day || "Monday",
        windows: g.windows.map((w) => ({
          start: formatTimeForInput(w.start),
          end: formatTimeForInput(w.end),
          last_sent: w.last_sent || null,
        })),
      };
    }
    return {
      branch: g.branch || "ALL",
      emails: Array.isArray(g.emails) ? g.emails : [],
      days: finalDays,
      weekly_day: g.weekly_day || "Monday",
      windows: [{
        start: formatTimeForInput(g.start),
        end: formatTimeForInput(g.end),
        last_sent: g.last_sent || null,
      }],
    };
  });

// Normalize an environment report schedule from the backend into editor shape:
const normalizeEnvironmentSchedule = (schedule) =>
  (Array.isArray(schedule) ? schedule : []).map((g) => {
    const rawDays = Array.isArray(g.days)
      ? g.days
      : g.days !== undefined && g.days !== null
        ? [g.days]
        : [1];
    const parsedDays = rawDays.map(Number).filter((d) => !isNaN(d) && d > 0).sort((a, b) => a - b);
    const finalDays = parsedDays.length > 0 ? parsedDays : [1];

    const meterIds = Array.isArray(g.meter_ids) ? g.meter_ids : [];
    const sensorId = meterIds.length === 1 ? meterIds[0] : (g.sensorId || "ALL");

    if (Array.isArray(g.windows)) {
      return {
        sensorId,
        label: g.label || (sensorId === "ALL" ? "ALL" : sensorId),
        meter_ids: meterIds,
        emails: Array.isArray(g.emails) ? g.emails : [],
        days: finalDays,
        weekly_day: g.weekly_day || "Monday",
        windows: g.windows.map((w) => ({
          start: formatTimeForInput(w.start),
          end: formatTimeForInput(w.end),
          last_sent: w.last_sent || null,
        })),
      };
    }
    return {
      sensorId,
      label: g.label || (sensorId === "ALL" ? "ALL" : sensorId),
      meter_ids: meterIds,
      emails: Array.isArray(g.emails) ? g.emails : [],
      days: finalDays,
      weekly_day: g.weekly_day || "Monday",
      windows: [{
        start: formatTimeForInput(g.start),
        end: formatTimeForInput(g.end),
        last_sent: g.last_sent || null,
      }],
    };
  });

const InfoTab = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBranch, machineToBranchMap, meterIdToBranchMap, branchList } = useBranch();

  // The Live and Insights tabs deep-link straight to a machine's settings
  // (see utils/nodeSettingsLink.js), so the requested section opens directly.
  const requestedSection = searchParams.get("section");
  const [activeSection, setActiveSection] = useState(() =>
    sections.some((section) => section.id === requestedSection)
      ? requestedSection
      : sections[0].id,
  );

  // Which machine card to open, and which of its fields to flash. Held in state
  // so the params can be cleared from the URL without losing the target — a
  // refresh should not re-trigger the highlight.
  const [focusTarget] = useState(() => {
    const meterId = searchParams.get("meter");
    const machineName = searchParams.get("machine");
    if (!meterId && !machineName) return null;
    return { meterId, machineName, focus: searchParams.get("focus") };
  });

  useEffect(() => {
    if (!focusTarget) return;
    // Drop the deep-link params once consumed so the highlight is a one-shot
    // and the URL the user can copy is just the settings page.
    if (searchParams.get("meter") || searchParams.get("machine")) {
      const next = new URLSearchParams(searchParams);
      ["meter", "machine", "focus", "section"].forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    }
  }, [focusTarget, searchParams, setSearchParams]);
  const logoutCheckRef = useRef(false);
  const isPremium = isPremiumUser();

  // Check if user is still logged in on mount - redirect to login if not
  useEffect(() => {
    if (logoutCheckRef.current) return; // Already checked
    logoutCheckRef.current = true;

    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    if (!token || !user) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // Memoize storedUser to break the infinite re-render loop
  const storedUser = useMemo(() => getStoredUser(), []);
  const username = storedUser?.username || "";
  const clientName = storedUser?.client_name || username;

  const [isRootUser, setIsRootUser] = useState(() => {
    const val = storedUser?.is_root_user;
    return val === true || val === 1 || String(val).toLowerCase() === "true";
  });

  const cachedMeterTypes = useMemo(() => readCachedMeterTypes(username), [username]);

  const [machines, setMachines] = useState([]);
  const [machineColorMap, setMachineColorMap] = useState({});
  const [machineLoading, setMachineLoading] = useState(true);
  const [metersLoaded, setMetersLoaded] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [clientEmailSuggestions, setClientEmailSuggestions] = useState([]);

  // Environment Report Schedule (environment_report_email)
  const [envSchedule, setEnvSchedule] = useState([]);
  const [envLoading, setEnvLoading] = useState(false);
  const [envSaving, setEnvSaving] = useState(false);
  const [envSensors, setEnvSensors] = useState([]);
  const [envSensorsLoading, setEnvSensorsLoading] = useState(false);

  const fetchEnvSensors = useCallback(async () => {
    if (!username) return;
    setEnvSensorsLoading(true);
    try {
      const res = await api.get(`/environment-sensors?username=${encodeURIComponent(username)}`);
      if (res.data && res.data.status === "success") {
        setEnvSensors(res.data.data || []);
      } else {
        setEnvSensors([]);
      }
    } catch (err) {
      console.error("Failed to fetch environment sensors:", err);
      try {
        const fallbackRes = await api.get(`/environment-sensors/live?username=${encodeURIComponent(username)}`);
        setEnvSensors(fallbackRes.data?.data || []);
      } catch {
        setEnvSensors([]);
      }
    } finally {
      setEnvSensorsLoading(false);
    }
  }, [username]);

  const fetchMachines = useCallback(async () => {
    if (!username) return;
    setMachineLoading(true);
    try {
      const data = await getClientMachines(username);
      const seen = new Set();
      const cleaned = (data.machines || []).filter((m) => {
        if (seen.has(m.machine_id)) return false; // skip duplicates
        seen.add(m.machine_id);
        return true;
      });
      setMachines(cleaned);
      setMachineColorMap(buildMachineColorMap(cleaned));
      setMetersLoaded(true);
    } catch (err) {
      console.error("Failed to fetch machines", err);
      setMetersLoaded(true);
    } finally {
      setMachineLoading(false);
    }
  }, [username]);

  // Load machines and environment sensors on mount
  useEffect(() => {
    if (username) {
      fetchMachines();
      fetchEnvSensors();
    }
  }, [username, fetchMachines, fetchEnvSensors]);

  useEffect(() => {
    if (username) {
      getClientEmails(username)
        .then((emails) => setClientEmailSuggestions(emails || []))
        .catch((err) => {
          console.error("Failed to fetch client emails", err);
          setClientEmailSuggestions([]);
        });
    }
  }, [username]);

  const hasEnvironmentMeter = useMemo(() => {
    if (metersLoaded) {
      return (
        machines.some(
          (m) => String(m.category || "").toUpperCase() === "ENVIRONMENT"
        ) || envSensors.length > 0
      );
    }
    if (cachedMeterTypes && typeof cachedMeterTypes.hasEnvironmentMeter === "boolean") {
      return cachedMeterTypes.hasEnvironmentMeter;
    }
    return false;
  }, [metersLoaded, machines, envSensors, cachedMeterTypes]);

  const hasEnergyMeter = useMemo(() => {
    if (metersLoaded) {
      return machines.some(
        (m) => String(m.category || "").toUpperCase() !== "ENVIRONMENT"
      );
    }
    if (cachedMeterTypes && typeof cachedMeterTypes.hasEnergyMeter === "boolean") {
      return cachedMeterTypes.hasEnergyMeter;
    }
    return true;
  }, [metersLoaded, machines, cachedMeterTypes]);

  useEffect(() => {
    if (metersLoaded && username) {
      const hasEnv =
        machines.some(
          (m) => String(m.category || "").toUpperCase() === "ENVIRONMENT"
        ) || envSensors.length > 0;
      const hasEnergy = machines.some(
        (m) => String(m.category || "").toUpperCase() !== "ENVIRONMENT"
      );
      writeCachedMeterTypes(username, {
        hasEnvironmentMeter: hasEnv,
        hasEnergyMeter: hasEnergy,
      });
    }
  }, [metersLoaded, username, machines, envSensors]);

  const availableSections = useMemo(() => {
    let list = sections;
    if (!isRootUser) {
      list = list.filter((s) => s.id !== "multiTier");
    }
    if (!hasEnergyMeter) {
      list = list.filter((s) => s.id !== "dataApi" && s.id !== "multiTier");
    }
    return list;
  }, [isRootUser, hasEnergyMeter]);

  useEffect(() => {
    if (!availableSections.some((s) => s.id === activeSection)) {
      setActiveSection(availableSections[0]?.id || "personal");
    }
  }, [availableSections, activeSection]);

  // ----------------------------------------------------------------------
  // Section: Personal Details
  // ----------------------------------------------------------------------
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [emailForm, setEmailForm] = useState({
    currentEmail: storedUser?.email || "",
    newEmail: "",
  });
  const [phoneForm, setPhoneForm] = useState({
    countryCode: "+92",
    phoneNumber: storedUser?.phone || "",
  });

  const [consumerForm, setConsumerForm] = useState({
    consumerId: "",
    sanctionedLoad: "",
    offlineMeterEmail: "",
    unitPrice: "",
    peakUnitPrice: "",
    genUnitPrice: "",
    solarUnitPrice: "",
  });

  // Branch email notification state
  const [branchEmailForm, setBranchEmailForm] = useState({
    email: "",
    branch: "ALL",
  });
  const [branches, setBranches] = useState([]);
  const [branchMeta, setBranchMeta] = useState({}); // { machine_name: CATEGORY } for option labels
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [emailList, setEmailList] = useState({});
  const [displayEmailList, setDisplayEmailList] = useState([]); // Flattened list for display
  const [emailLoading, setEmailLoading] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null); // { originalEmail: string, newEmail: string, branch: string }
  const [offlineEmailInput, setOfflineEmailInput] = useState("");
  const [editingOfflineEmail, setEditingOfflineEmail] = useState(null); // string or null

  // Energy (Daily) Report Schedule — grouped by branch (daily_report_email):
  // [{ branch, emails, days, windows: [{ start, end, last_sent }] }]
  const [reportSchedule, setReportSchedule] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportSaving, setReportSaving] = useState(false);

  // Energy Efficiency Report Schedule — same grouped shape as the daily report.
  const [effSchedule, setEffSchedule] = useState([]);
  const [effLoading, setEffLoading] = useState(false);
  const [effSaving, setEffSaving] = useState(false);


  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const toastTimer = useRef(null);

  // ----------------------------------------------------------------------
  // Section: Data API
  // ----------------------------------------------------------------------
  // A copy-paste-ready request for THIS account, so the manual's generic
  // example does not have to be translated by hand. The password is left as a
  // placeholder on purpose — it is never rendered into the page.
  const dataApiExampleUrl = useMemo(() => {
    const base = DATA_API_BASE.replace(/\/$/, "");
    const day = (offset) =>
      new Date(Date.now() - offset * 86400000).toISOString().slice(0, 10);
    const params = new URLSearchParams({
      username: username || "your_username",
      password: "YOUR_PASSWORD",
      mode: "hour",
      start: day(1),
      end: day(0),
    });
    return `${base}/client_data?${params.toString()}`;
  }, [username]);

  const handleCopyDataApiUrl = async () => {
    try {
      await navigator.clipboard.writeText(dataApiExampleUrl);
      showToast("Request URL copied to clipboard", "success");
    } catch (error) {
      showToast("Could not copy automatically. Select the URL and copy it.", "error");
    }
  };

  const showToast = (message, type = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ show: true, message, type });
    toastTimer.current = setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3500);
  };

  // Fetch consumer info on mount
  useEffect(() => {
    if (username) {
      getConsumerInfo(username)
        .then((data) => {
          setConsumerForm({
            consumerId: data.consumer_id || "",
            sanctionedLoad: data.sanctioned_load || "",
            offlineMeterEmail: data.offline_meter_email || "",
            unitPrice: data.unit_price || "",
            peakUnitPrice: data.peak_unit_price || "",
            genUnitPrice: data.gen_unitPrice || data.gen_unitprice || "",
            solarUnitPrice: data.solar_unitPrice || data.solar_unitprice || "",
          });
          if (data.is_root_user !== undefined) {
            const isRoot = data.is_root_user === true || data.is_root_user === 1 || String(data.is_root_user).toLowerCase() === "true";
            setIsRootUser(isRoot);
          }
        })
        .catch(() => {
          // Consumer info not found yet, keep defaults
        });
    }
  }, [username]);

  // Fetch hierarchy data for branch/main targets.
  // Surface grouping nodes (VIRTUAL / GRID) and MAIN supply meters; exclude
  // SOLAR, GENERATOR and leaf MACHINE nodes. Whole system is the "ALL" option.
  useEffect(() => {
    if (username && hasEnergyMeter) {
      setBranchesLoading(true);
      api.get(`/hierarchy-tree?username=${encodeURIComponent(username)}`)
        .then((res) => {
          const data = res.data;
          const TARGET_CATEGORIES = new Set(["VIRTUAL", "GRID", "MAIN"]);
          // Also drop solar/generator aggregates that slip through as VIRTUAL
          // nodes (e.g. "Main and Solar").
          const EXCLUDE_NAME = /solar|generator/i;
          const names = [];
          const meta = {};

          const walk = (node, isRoot) => {
            if (!node) return;
            const cat = (node.category || "").toUpperCase();
            const name = node.machine_name || "";
            // Skip the root (covered by "Main / Whole System"), leaf machines,
            // and any solar/generator node (by category or name).
            if (!isRoot && name && TARGET_CATEGORIES.has(cat) && !EXCLUDE_NAME.test(name)) {
              if (!(node.machine_name in meta)) {
                names.push(node.machine_name);
                meta[node.machine_name] = cat;
              }
            }
            const children = Array.isArray(node.children)
              ? node.children
              : (node.children && typeof node.children === "object"
                ? Object.values(node.children)
                : []);
            children.forEach((c) => walk(c, false));
          };

          walk(data, true);

          setBranchMeta(meta);
          setBranches(["ALL", ...names]);
        })
        .catch((error) => {
          console.error("Error fetching hierarchy-tree:", error);
          setBranchMeta({});
          setBranches(["ALL"]); // At least show ALL option
        })
        .finally(() => {
          setBranchesLoading(false);
        });
    } else if (!hasEnergyMeter) {
      setBranches(["ALL"]);
      setBranchMeta({});
      setBranchesLoading(false);
    }
  }, [username, hasEnergyMeter]);

  const fetchEmails = async () => {
    setEmailLoading(true);
    try {
      const data = await getEmails(username);
      if (data && data.emails) {
        setEmailList(data.emails);

        // Flatten for easier display
        const flatList = [];
        Object.entries(data.emails).forEach(([branch, emails]) => {
          emails.forEach(email => {
            flatList.push({ branch, email });
          });
        });
        setDisplayEmailList(flatList);
      }
    } catch (error) {
      console.error("Failed to fetch emails:", error);
    } finally {
      setEmailLoading(false);
    }
  };

  useEffect(() => {
    if (username) {
      fetchEmails();
    }
  }, [username]);

  const handleConsumerInfoUpdate = async () => {
    if (!consumerForm.consumerId.trim()) {
      showToast("Please enter a Consumer ID.", "error");
      return;
    }
    if (
      !consumerForm.sanctionedLoad ||
      Number(consumerForm.sanctionedLoad) <= 0
    ) {
      showToast("Please enter a valid Sanctioned Load.", "error");
      return;
    }
    try {
      const res = await updateConsumerInfo(
        username,
        consumerForm.consumerId,
        consumerForm.sanctionedLoad,
        consumerForm.offlineMeterEmail,
      );
      showToast(
        res.message || "Customer information saved successfully",
        "success",
      );
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Failed to update customer information",
        "error",
      );
    }
  };

  const handleAddOfflineMeterEmail = async () => {
    if (!offlineEmailInput.trim()) {
      showToast("Please enter an email address for offline alerts.", "error");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(offlineEmailInput.trim())) {
      showToast("Please enter a valid email address.", "error");
      return;
    }

    try {
      const res = await updateOfflineMeterEmail(
        username,
        offlineEmailInput.trim(),
      );
      setConsumerForm((prev) => ({ ...prev, offlineMeterEmail: offlineEmailInput.trim() }));
      setOfflineEmailInput("");
      showToast(
        res.message || "Offline meter alert email saved successfully",
        "success",
      );
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Failed to save offline meter alert email",
        "error",
      );
    }
  };

  const startEditingOfflineEmail = () => {
    setEditingOfflineEmail(consumerForm.offlineMeterEmail || "");
  };

  const cancelEditingOfflineEmail = () => {
    setEditingOfflineEmail(null);
  };

  const saveEditedOfflineEmail = async () => {
    if (!editingOfflineEmail || !editingOfflineEmail.trim()) {
      showToast("Email address cannot be empty.", "error");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editingOfflineEmail.trim())) {
      showToast("Please enter a valid email address.", "error");
      return;
    }

    try {
      const res = await updateOfflineMeterEmail(
        username,
        editingOfflineEmail.trim(),
      );
      setConsumerForm((prev) => ({ ...prev, offlineMeterEmail: editingOfflineEmail.trim() }));
      setEditingOfflineEmail(null);
      showToast(
        res.message || "Offline meter alert email updated successfully",
        "success",
      );
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Failed to update offline meter alert email",
        "error",
      );
    }
  };

  const handleDeleteOfflineMeterEmail = async () => {
    try {
      const res = await deleteOfflineMeterEmail(username);
      setConsumerForm((prev) => ({ ...prev, offlineMeterEmail: "" }));
      setEditingOfflineEmail(null);
      showToast(
        res.message || "Offline meter alert email deleted successfully",
        "success",
      );
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Failed to delete offline meter alert email",
        "error",
      );
    }
  };

  const handleUnitPricesUpdate = async () => {
    try {
      const res = await updateUnitPrices(
        username,
        consumerForm.unitPrice,
        consumerForm.genUnitPrice,
        consumerForm.solarUnitPrice,
        consumerForm.peakUnitPrice,
      );
      showToast(
        res.message || "Unit prices updated successfully",
        "success",
      );
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Failed to update unit prices",
        "error",
      );
    }
  };

  const handlePasswordUpdate = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast("New password and confirm password do not match.", "error");
      return;
    }
    try {
      const res = await updatePassword(
        username,
        passwordForm.oldPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword,
      );
      showToast(res.message || "Password updated successfully", "success");
      setPasswordForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to update password",
        "error",
      );
    }
  };

  const handleEmailUpdate = async () => {
    if (!emailForm.currentEmail.trim()) {
      showToast("Please enter your current email.", "error");
      return;
    }
    if (!emailForm.newEmail.trim()) {
      showToast("Please enter the new email.", "error");
      return;
    }
    try {
      const res = await updateEmailAddress(
        username,
        emailForm.currentEmail.trim(),
        emailForm.newEmail.trim(),
      );
      showToast(res.message || "Email updated successfully", "success");
      setEmailForm({ currentEmail: emailForm.newEmail.trim(), newEmail: "" });
    } catch (error) {
      showToast(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to update email",
        "error",
      );
    }
  };

  const handlePhoneUpdate = async () => {
    try {
      const res = await updatePhone(
        username,
        phoneForm.countryCode,
        phoneForm.phoneNumber,
      );
      showToast(res.message || "Phone number updated successfully", "success");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to update phone number",
        "error",
      );
    }
  };

  const handleBranchEmailUpdate = async () => {
    // Validate email
    if (!branchEmailForm.email.trim()) {
      showToast("Please enter an email address.", "error");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(branchEmailForm.email)) {
      showToast("Please enter a valid email address.", "error");
      return;
    }

    // Validate branch selection
    if (!branchEmailForm.branch) {
      showToast("Please select a branch.", "error");
      return;
    }

    try {
      const data = await addEmail(username, branchEmailForm.email, branchEmailForm.branch);
      showToast(
        data.message || "Email added successfully for branch notifications",
        "success",
      );
      // Reset form and refresh list
      setBranchEmailForm({ email: "", branch: "ALL" });
      fetchEmails();
    } catch (error) {
      showToast(error.response?.data?.detail || error.message || "Failed to add email for branch", "error");
    }
  };

  const handleDeleteEmail = async (email, branch) => {
    if (!window.confirm(`Are you sure you want to delete ${email} from ${branch}?`)) return;

    try {
      const data = await deleteEmail(username, email, branch);
      showToast(data.message || "Email deleted successfully", "success");
      fetchEmails();
    } catch (error) {
      showToast(error.response?.data?.detail || "Failed to delete email", "error");
    }
  };

  const startEditingEmail = (email, branch) => {
    setEditingEmail({ originalEmail: email, newEmail: email, branch });
  };

  const cancelEditingEmail = () => {
    setEditingEmail(null);
  };

  const saveEditedEmail = async () => {
    if (!editingEmail || !editingEmail.newEmail.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editingEmail.newEmail)) {
      showToast("Please enter a valid email address.", "error");
      return;
    }

    if (editingEmail.newEmail === editingEmail.originalEmail) {
      setEditingEmail(null);
      return;
    }

    try {
      const data = await updateEmailAddress(username, editingEmail.originalEmail, editingEmail.newEmail);
      showToast(data.message || "Email updated successfully", "success");
      setEditingEmail(null);
      fetchEmails();
    } catch (error) {
      showToast(error.response?.data?.detail || "Failed to update email", "error");
    }
  };

  // ----------------------------------------------------------------------
  // Section: Report Windows Helpers & Handlers
  // ----------------------------------------------------------------------

  const fetchReportSchedule = useCallback(async () => {
    if (!username) return;
    setReportLoading(true);
    try {
      const schedule = await getDailyReportSchedule(username);
      setReportSchedule(normalizeSchedule(schedule));
    } catch (error) {
      console.error("Failed to fetch report schedule:", error);
      setReportSchedule([]);
    } finally {
      setReportLoading(false);
    }
  }, [username]);

  const fetchEffSchedule = useCallback(async () => {
    if (!username) return;
    setEffLoading(true);
    try {
      const schedule = await getEnergyEfficiencySchedule(username);
      setEffSchedule(normalizeSchedule(schedule));
    } catch (error) {
      console.error("Failed to fetch energy efficiency schedule:", error);
      setEffSchedule([]);
    } finally {
      setEffLoading(false);
    }
  }, [username]);

  const fetchEnvSchedule = useCallback(async () => {
    if (!username) return;
    setEnvLoading(true);
    try {
      const schedule = await getEnvironmentReportSchedule(username);
      setEnvSchedule(normalizeEnvironmentSchedule(schedule));
    } catch (error) {
      console.error("Failed to fetch environment report schedule:", error);
      setEnvSchedule([]);
    } finally {
      setEnvLoading(false);
    }
  }, [username]);

  const [profileSettings, setProfileSettings] = useState({
    sanctioned_load: null,
    sanctioned_load_on: false,
    minimum_threshold: null,
    minimum_threshold_on: false,
  });
  const [localThreshold, setLocalThreshold] = useState("");
  const [togglingField, setTogglingField] = useState(null);

  const fetchProfileSettings = useCallback(async () => {
    if (!username) return;
    try {
      const data = await getClientProfileSettings(username);
      setProfileSettings(data);
      setLocalThreshold(data.minimum_threshold ?? "");
    } catch (e) {
      console.error("Failed to fetch profile settings:", e);
    }
  }, [username]);

  useEffect(() => {
    if (activeSection === "notifications" && username) {
      if (hasEnergyMeter) {
        fetchReportSchedule();
        fetchProfileSettings();
        fetchEffSchedule();
      }
      if (hasEnvironmentMeter) {
        fetchEnvSchedule();
        fetchEnvSensors();
      }
    }
  }, [
    activeSection,
    username,
    hasEnergyMeter,
    hasEnvironmentMeter,
    fetchReportSchedule,
    fetchProfileSettings,
    fetchEffSchedule,
    fetchEnvSchedule,
    fetchEnvSensors,
  ]);

  // ---- Branch-grouped schedule save handlers (report + efficiency) ----
  // Each branch needs >=1 recipient and >=1 complete time window.
  const validateBranchSchedule = (schedule) => {
    for (const g of schedule || []) {
      const label = g.branch === "ALL" ? "Main" : g.branch;
      if (!g.emails || g.emails.length === 0) {
        return `${label}: add at least one recipient`;
      }
      if (!g.days || g.days.length === 0) {
        return `${label}: select at least one report period (Daily, Weekly, 15 Days, or Monthly)`;
      }
      if (!g.windows || g.windows.length === 0) {
        return `${label}: add at least one time window`;
      }
      if (g.windows.some((w) => !w.start || !w.end)) {
        return `${label}: set start and end on every time window`;
      }
    }
    return null;
  };

  const handleSaveReportSchedule = async () => {
    const err = validateBranchSchedule(reportSchedule);
    if (err) {
      showToast(err, "error");
      return;
    }
    setReportSaving(true);
    try {
      await saveDailyReportSchedule(username, reportSchedule);
      showToast("Energy report schedule saved", "success");
      fetchReportSchedule();
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Failed to save report schedule",
        "error",
      );
    } finally {
      setReportSaving(false);
    }
  };

  const handleSaveEffSchedule = async () => {
    const err = validateBranchSchedule(effSchedule);
    if (err) {
      showToast(err, "error");
      return;
    }
    setEffSaving(true);
    try {
      await saveEnergyEfficiencySchedule(username, effSchedule);
      showToast("Energy efficiency schedule saved", "success");
      fetchEffSchedule();
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Failed to save schedule",
        "error",
      );
    } finally {
      setEffSaving(false);
    }
  };

  const validateEnvironmentSchedule = (schedule) => {
    for (const g of schedule || []) {
      const label = g.label || (g.sensorId === "ALL" || !g.sensorId ? "All Sensors" : g.sensorId);
      if (!g.emails || g.emails.length === 0) {
        return `${label}: add at least one recipient`;
      }
      if (!g.days || g.days.length === 0) {
        return `${label}: select at least one report period (Daily, Weekly, 15 Days, or Monthly)`;
      }
      const isDaily = (g.days || []).includes(1);
      if (isDaily) {
        if (!g.windows || g.windows.length === 0) {
          return `${label}: add at least one time window for daily report`;
        }
        if (g.windows.some((w) => !w.start || !w.end)) {
          return `${label}: set start and end on every time window`;
        }
      }
    }
    return null;
  };

  const handleSaveEnvSchedule = async () => {
    const err = validateEnvironmentSchedule(envSchedule);
    if (err) {
      showToast(err, "error");
      return;
    }
    setEnvSaving(true);
    try {
      await saveEnvironmentReportSchedule(username, envSchedule);
      showToast("Environment report schedule saved", "success");
      fetchEnvSchedule();
    } catch (error) {
      console.error("Failed to save environment report schedule:", error);
      showToast(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to save environment schedule",
        "error",
      );
    } finally {
      setEnvSaving(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section: Logo Upload
  // ----------------------------------------------------------------------
  const [logoImage, setLogoImage] = useState(null);
  const [logoDirty, setLogoDirty] = useState(false);
  const [logoLoading, setLogoLoading] = useState(true);
  const [logoExists, setLogoExists] = useState(false);
  const [remoteLogo, setRemoteLogo] = useState(null);
  const fileInputRef = useRef(null);

  const currentLogoDisplay = logoImage || remoteLogo;

  // Fetch logo on mount
  useEffect(() => {
    if (username) {
      setLogoLoading(true);
      const apiBase = getApiBase() || "/api";
      fetch(`${apiBase}/get_logo?username=${encodeURIComponent(username)}`)
        .then((response) => {
          if (response.ok) {
            return response.blob();
          } else if (response.status === 404) {
            // No logo found
            setLogoExists(false);
            setRemoteLogo(null);
            return null;
          } else {
            throw new Error("Failed to fetch logo");
          }
        })
        .then((blob) => {
          if (blob) {
            const logoUrl = URL.createObjectURL(blob);
            setRemoteLogo(logoUrl);
            setLogoExists(true);
          }
        })
        .catch((error) => {
          console.error("Error fetching logo:", error);
          setLogoExists(false);
          setRemoteLogo(null);
        })
        .finally(() => {
          setLogoLoading(false);
        });
    }
  }, [username]);

  const handleLogoSelect = (file) => {
    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      showToast("Please select a PNG, JPG, or JPEG image.", "error");
      return;
    }

    // Validate file size (max 100KB)
    const maxSize = 100 * 1024; // 100KB in bytes
    if (file.size > maxSize) {
      showToast(
        "File size must be less than 100KB. Please select a smaller image.",
        "error",
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const nextLogo = event.target?.result;
      if (typeof nextLogo === "string") {
        setLogoImage(nextLogo);
        setLogoDirty(true);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLogoSubmit = async () => {
    if (!logoImage) {
      showToast("Please select an image first.", "error");
      return;
    }

    try {
      const blob = await fetch(logoImage).then((res) => res.blob());

      // Validate blob size again before upload
      if (blob.size > 100 * 1024) {
        showToast(
          "File size must be less than 100KB. Please select a smaller image.",
          "error",
        );
        return;
      }

      const formData = new FormData();
      formData.append("file", blob, "logo.png");
      formData.append("username", username);

      const apiBase = getApiBase() || "/api";
      const response = await fetch(`${apiBase}/upload_logo`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setLogoDirty(false);
        showToast("Logo uploaded successfully!", "success");

        // Clear the preview image
        setLogoImage(null);

        // Automatically refresh the page so that the Header and all other
        // components fetch the updated logo.
        setTimeout(() => window.location.reload(), 1500);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to upload logo");
      }
    } catch (error) {
      console.error("Upload error:", error);
      showToast("Error uploading logo: " + error.message, "error");
    }
  };

  // ----------------------------------------------------------------------
  // Section: Machine Settings
  // ----------------------------------------------------------------------


  const handleMachineUpdate = async (machineId, updatedFields) => {
    const machine = machines.find((m) => m.machine_id === machineId);
    if (!machine) return;

    try {
      await updateMachineConfig(machine.meter_id, {
        username,
        ...updatedFields,
      });
      // Update local state
      setMachines((prev) => {
        const updated = prev.map((m) =>
          m.machine_id === machineId ? { ...m, ...updatedFields } : m,
        );
        // Rebuild color map so the header dot refreshes immediately
        setMachineColorMap(buildMachineColorMap(updated));
        return updated;
      });
      showToast("Machine configuration updated successfully", "success");
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Failed to update machine configuration",
        "error",
      );
    }
  };

  // ----------------------------------------------------------------------
  // Section: Notifications
  // ----------------------------------------------------------------------

  const handleToggleSetting = async (field, extraPayload) => {
    const newVal = !profileSettings[field];
    setProfileSettings(prev => ({ ...prev, [field]: newVal }));
    setTogglingField(field);
    try {
      await updateClientProfileSettings(username, { [field]: newVal, ...extraPayload });
      showToast("Setting updated successfully", "success");
    } catch (e) {
      setProfileSettings(prev => ({ ...prev, [field]: !newVal }));
      showToast("Failed to update setting", "error");
    } finally {
      setTogglingField(null);
    }
  };

  // ----------------------------------------------------------------------
  // Section: Multi Tier (Add Credentials)
  // ----------------------------------------------------------------------
  const [multiTierForm, setMultiTierForm] = useState({
    username: "",
    password: "",
    machineName: "",
    meterId: "",
    excludedIds: [],
  });
  const [mtMachinesTree, setMtMachinesTree] = useState([]);
  const [mtSelectedNodeObj, setMtSelectedNodeObj] = useState(null);
  const [mtLoading, setMtLoading] = useState(false);
  const [addedCredentials, setAddedCredentials] = useState([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  useEffect(() => {
    if (isRootUser && activeSection === "multiTier" && username) {
      setMtLoading(true);
      api.get(`/hierarchy-tree?username=${encodeURIComponent(username)}`)
        .then((res) => {
          const data = res.data;

          // Helper to transform backend tree to NestedMachineSelector format
          const transformTree = (node) => {
            if (!node) return [];
            const rawChildren = Array.isArray(node.children)
              ? node.children
              : (node.children ? Object.values(node.children) : []);

            return rawChildren.map(child => ({
              name: child.machine_name,
              category: child.category,
              meter_id: child.meter_id,
              children: transformTree(child),
              // pass raw object too, purely to power ExcludeNodeTree later
              rawNode: child
            }));
          };

          setMtMachinesTree(transformTree(data));
        })
        .catch((err) => {
          console.error("Error fetching multi tier machines:", err);
          showToast("Failed to load machines", "error");
        })
        .finally(() => {
          setMtLoading(false);
        });
    }
  }, [isRootUser, activeSection, username]);

  // Fetch all sub-users under this client
  const fetchSubUsers = useCallback(() => {
    if (isRootUser && activeSection === "multiTier" && clientName) {
      setCredentialsLoading(true);
      api.get(`/get_sub_users?client_name=${encodeURIComponent(clientName)}`)
        .then((res) => {
          const users = Array.isArray(res.data) ? res.data : [];
          setAddedCredentials(users);
        })
        .catch((err) => {
          console.error("Error fetching sub-users:", err);
          setAddedCredentials([]);
        })
        .finally(() => {
          setCredentialsLoading(false);
        });
    }
  }, [isRootUser, activeSection, clientName]);

  useEffect(() => {
    fetchSubUsers();
  }, [fetchSubUsers]);

  const handleAddCredentials = async () => {
    if (!multiTierForm.username || !multiTierForm.password || !multiTierForm.meterId) {
      showToast("Please fill all fields", "error");
      return;
    }

    try {
      const payload = {
        client_name: clientName,
        username: multiTierForm.username,
        password: multiTierForm.password,
        email: `${multiTierForm.username}@example.com`,
        is_root_user: false,
        scope_meter_id: multiTierForm.meterId,
        exclude_scope_meter_id: multiTierForm.excludedIds.length > 0 ? multiTierForm.excludedIds.join(',') : null,
      };

      await api.post("/add_user", payload);

      showToast("Credentials added successfully", "success");

      // Refetch sub-users list
      fetchSubUsers();

      setMultiTierForm({ username: "", password: "", machineName: "", meterId: "", excludedIds: [] });
      setMtSelectedNodeObj(null);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Error adding credentials", "error");
    }
  };

  // ----------------------------------------------------------------------
  // Renderers
  // ----------------------------------------------------------------------
  const renderSectionContent = () => {
    switch (activeSection) {
      case "personal":
        return (
          <div className="info-content-card">
            <header style={contentHeaderStyle}>
              <div>
                <h2 style={contentTitleStyle}>Details</h2>
                <p
                  style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: 14 }}
                >
                  Manage your account information, consumer details, and
                  branding
                </p>
              </div>
            </header>

            <div className="info-personal-groups">
              {hasEnergyMeter && (
                <section className="info-personal-group">
                  <div className="info-personal-group-header">
                    <h3 style={sectionBlockTitleStyle}>Consumer Energy Details</h3>
                    <p style={sectionBlockDescriptionStyle}>Consumer details and pricing</p>
                  </div>
                  <div className="info-personal-subsections">
                    {/* Consumer Information */}
                    <div className="info-personal-block">
                      <h3 style={sectionBlockTitleStyle}>Consumer Information</h3>
                      <div style={formGridStyle}>
                        <label style={inputGroupStyle}>
                          <FieldHelp id="info.consumerId">Consumer ID</FieldHelp>
                          <input
                            type="text"
                            value={consumerForm.consumerId}
                            onChange={(e) =>
                              setConsumerForm({
                                ...consumerForm,
                                consumerId: e.target.value,
                              })
                            }
                            style={textInputStyle}
                            placeholder="Enter your Consumer ID"
                          />
                        </label>
                        <label style={inputGroupStyle}>
                          <FieldHelp id="info.sanctionedLoad">Sanctioned Load (kW)</FieldHelp>
                          <input
                            type="number"
                            step="0.1"
                            value={consumerForm.sanctionedLoad}
                            onChange={(e) =>
                              setConsumerForm({
                                ...consumerForm,
                                sanctionedLoad: e.target.value,
                              })
                            }
                            style={textInputStyle}
                            placeholder="e.g. 5.0"
                          />
                        </label>
                      </div>
                      <div style={{ marginTop: 12, textAlign: "right" }}>
                        <button
                          type="button"
                          onClick={handleConsumerInfoUpdate}
                          style={primaryButtonStyle}
                        >
                          Update Details
                        </button>
                      </div>
                    </div>

                    {/* Unit Prices */}
                    <div className="info-personal-block">
                      <h3 style={sectionBlockTitleStyle}>Unit Prices</h3>
                      <div style={formGridStyle}>
                        <label style={inputGroupStyle}>
                          <FieldHelp id="info.gridUnitPrice">Grid Unit Price</FieldHelp>
                          <input
                            type="number"
                            step="1"
                            value={consumerForm.unitPrice}
                            onChange={(e) =>
                              setConsumerForm({
                                ...consumerForm,
                                unitPrice: e.target.value,
                              })
                            }
                            style={textInputStyle}
                            placeholder="e.g. 50"
                          />
                        </label>
                        <label style={inputGroupStyle}>
                          <FieldHelp id="info.gridPeakUnitPrice">Grid Peak Unit Price</FieldHelp>
                          <input
                            type="number"
                            step="1"
                            value={consumerForm.peakUnitPrice}
                            onChange={(e) =>
                              setConsumerForm({
                                ...consumerForm,
                                peakUnitPrice: e.target.value,
                              })
                            }
                            style={textInputStyle}
                            placeholder="e.g. 80"
                          />
                        </label>
                        <label style={inputGroupStyle}>
                          <FieldHelp id="info.genUnitPrice">Generator Unit Price</FieldHelp>
                          <input
                            type="number"
                            step="1"
                            value={consumerForm.genUnitPrice}
                            onChange={(e) =>
                              setConsumerForm({
                                ...consumerForm,
                                genUnitPrice: e.target.value,
                              })
                            }
                            style={textInputStyle}
                            placeholder="e.g. 80"
                          />
                        </label>
                        <label style={inputGroupStyle}>
                          <FieldHelp id="info.solarUnitPrice">Solar Unit Price</FieldHelp>
                          <input
                            type="number"
                            step="1"
                            value={consumerForm.solarUnitPrice}
                            onChange={(e) =>
                              setConsumerForm({
                                ...consumerForm,
                                solarUnitPrice: e.target.value,
                              })
                            }
                            style={textInputStyle}
                            placeholder="e.g. 25"
                          />
                        </label>
                      </div>
                      <div style={{ marginTop: 12, textAlign: "right" }}>
                        <button
                          type="button"
                          onClick={handleUnitPricesUpdate}
                          style={primaryButtonStyle}
                        >
                          Update Prices
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              <section className="info-personal-group">
                <div className="info-personal-group-header">
                  <h3 style={sectionBlockTitleStyle}>Security & Contact</h3>
                  <p style={sectionBlockDescriptionStyle}>Password, phone, and email notification settings</p>
                </div>

                {/* Password Update */}
                <div className="info-personal-block">
                  <h3 style={sectionBlockTitleStyle}>Update Password</h3>
                  <div style={formGridStyle}>
                    <label style={inputGroupStyle}>
                      <FieldHelp id="info.oldPassword">Old Password</FieldHelp>
                      <input
                        type="password"
                        value={passwordForm.oldPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            oldPassword: e.target.value,
                          })
                        }
                        style={textInputStyle}
                      />
                    </label>
                    <label style={inputGroupStyle}>
                      <FieldHelp id="info.newPassword">New Password</FieldHelp>
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            newPassword: e.target.value,
                          })
                        }
                        style={textInputStyle}
                      />
                    </label>
                    <label style={inputGroupStyle}>
                      <FieldHelp id="info.confirmPassword">Confirm New Password</FieldHelp>
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            confirmPassword: e.target.value,
                          })
                        }
                        style={textInputStyle}
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={handlePasswordUpdate}
                      style={primaryButtonStyle}
                    >
                      Update Password
                    </button>
                  </div>
                </div>

                {/* Phone Update (Premium Feature) */}
                <div
                  className="info-personal-block"
                  style={{
                    position: "relative",
                    opacity: isPremium ? 1 : 0.55,
                    pointerEvents: isPremium ? "auto" : "none",
                    userSelect: isPremium ? "auto" : "none",
                  }}
                  aria-disabled={!isPremium}
                >
                  {!isPremium && (
                    <div
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: "linear-gradient(135deg, #f59e0b, #d97706)",
                        color: "#fff",
                        padding: "4px 10px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        pointerEvents: "auto",
                        zIndex: 2,
                      }}
                      title="Upgrade to Premium to unlock this feature"
                    >
                      PREMIUM FEATURE
                    </div>
                  )}
                  <h3 style={sectionBlockTitleStyle}>Update Phone Number</h3>
                  <div style={formGridStyle}>
                    <label style={inputGroupStyle}>
                      <FieldHelp id="info.countryCode">Country Code</FieldHelp>
                      <select
                        value={phoneForm.countryCode}
                        onChange={(e) =>
                          setPhoneForm({
                            ...phoneForm,
                            countryCode: e.target.value,
                          })
                        }
                        style={textInputStyle}
                      >
                        <option value="+92">+92 (Pakistan)</option>
                        <option value="+1">+1 (USA)</option>
                        <option value="+44">+44 (UK)</option>
                      </select>
                    </label>
                    <label style={inputGroupStyle}>
                      <FieldHelp id="info.phoneNumber">Phone Number</FieldHelp>
                      <input
                        type="tel"
                        value={phoneForm.phoneNumber}
                        onChange={(e) =>
                          setPhoneForm({
                            ...phoneForm,
                            phoneNumber: e.target.value,
                          })
                        }
                        style={textInputStyle}
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={handlePhoneUpdate}
                      style={primaryButtonStyle}
                    >
                      Update Phone
                    </button>
                  </div>
                </div>

                {/* Offline Meter Alerts Email */}
                <div className="info-personal-block" style={{ position: "relative" }}>
                  <h3 style={sectionBlockTitleStyle}>Offline Meter Alerts</h3>
                  <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
                    Configure the email address to receive immediate notifications when meters go offline or stop reporting data
                  </p>
                  <div style={formGridStyle}>
                    <label style={inputGroupStyle}>
                      <FieldHelp id="info.offlineAlertEmail">Email Address</FieldHelp>
                      <input
                        type="email"
                        value={offlineEmailInput}
                        onChange={(e) => setOfflineEmailInput(e.target.value)}
                        style={textInputStyle}
                        placeholder="Enter email address for offline alerts"
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: 12, textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={handleAddOfflineMeterEmail}
                      style={primaryButtonStyle}
                    >
                      {consumerForm.offlineMeterEmail ? "Update Alert Email" : "Add Offline Alert Email"}
                    </button>
                  </div>

                  {/* Offline Email List */}
                  <div style={{ marginTop: 16 }}>
                    {!consumerForm.offlineMeterEmail ? (
                      <p style={{ color: "#94a3b8", fontSize: 14 }}>No offline alert email configured yet.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{
                          padding: '10px 14px',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: 14
                        }}>
                          {editingOfflineEmail !== null ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                              <input
                                type="email"
                                value={editingOfflineEmail}
                                onChange={(e) => setEditingOfflineEmail(e.target.value)}
                                style={{ ...textInputStyle, padding: '6px 8px', flex: 1 }}
                              />
                              <button
                                onClick={saveEditedOfflineEmail}
                                style={{ ...primaryButtonStyle, padding: '6px 12px', fontSize: 12, background: '#16a34a' }}
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEditingOfflineEmail}
                                style={{ ...primaryButtonStyle, padding: '6px 12px', fontSize: 12, background: '#94a3b8' }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontWeight: 500, color: '#334155' }}>{consumerForm.offlineMeterEmail}</span>
                                <span style={{ fontSize: 12, color: '#64748b' }}>Recipient: Offline Meter Alerts</span>
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                  onClick={startEditingOfflineEmail}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#2563eb',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    fontWeight: 500
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={handleDeleteOfflineMeterEmail}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    fontWeight: 500
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Email Notifications by Branch */}
                {hasEnergyMeter && (
                  <div className="info-personal-block" style={{ position: "relative" }}>
                    <h3 style={sectionBlockTitleStyle}>
                      Email Notifications by Branch
                    </h3>
                    <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
                      Add email addresses to receive notifications for specific
                      branches
                    </p>
                    <div style={formGridStyle}>
                      <label style={inputGroupStyle}>
                        <FieldHelp id="info.branchEmail">Email Address</FieldHelp>
                        <input
                          type="email"
                          value={branchEmailForm.email}
                          onChange={(e) =>
                            setBranchEmailForm({
                              ...branchEmailForm,
                              email: e.target.value,
                            })
                          }
                          style={textInputStyle}
                          placeholder="Enter email address"
                        />
                      </label>
                      <label style={inputGroupStyle}>
                        <FieldHelp id="info.branchEmailBranch">Branch</FieldHelp>
                        <select
                          value={branchEmailForm.branch}
                          onChange={(e) =>
                            setBranchEmailForm({
                              ...branchEmailForm,
                              branch: e.target.value,
                            })
                          }
                          style={textInputStyle}
                          disabled={branchesLoading}
                        >
                          {branchesLoading && (
                            <option value="ALL">Loading branches...</option>
                          )}
                          {branches.map((branch) => (
                            <option key={branch} value={branch}>
                              {branch}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div style={{ marginTop: 12, textAlign: "right" }}>
                      <button
                        type="button"
                        onClick={handleBranchEmailUpdate}
                        style={primaryButtonStyle}
                        disabled={branchesLoading}
                      >
                        Add Email for Branch
                      </button>
                    </div>

                    {/* Email List */}
                    {emailLoading ? (
                      <p>Loading emails...</p>
                    ) : (
                      <div style={{ marginTop: 16 }}>
                        {displayEmailList.length === 0 ? (
                          <p style={{ color: "#94a3b8", fontSize: 14 }}>No emails added yet.</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {displayEmailList.map((item, index) => {
                              const isEditing = editingEmail && editingEmail.originalEmail === item.email && editingEmail.branch === item.branch;

                              return (
                                <div key={`${item.branch}-${item.email}-${index}`} style={{
                                  padding: '10px 14px',
                                  background: '#f8fafc',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: 8,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  fontSize: 14
                                }}>
                                  {isEditing ? (
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                                      <input
                                        type="email"
                                        value={editingEmail.newEmail}
                                        onChange={(e) => setEditingEmail({ ...editingEmail, newEmail: e.target.value })}
                                        style={{ ...textInputStyle, padding: '6px 8px', flex: 1 }}
                                      />
                                      <button
                                        onClick={saveEditedEmail}
                                        style={{ ...primaryButtonStyle, padding: '6px 12px', fontSize: 12, background: '#16a34a' }}
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={cancelEditingEmail}
                                        style={{ ...primaryButtonStyle, padding: '6px 12px', fontSize: 12, background: '#94a3b8' }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <>
                                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontWeight: 500, color: '#334155' }}>{item.email}</span>
                                        <span style={{ fontSize: 12, color: '#64748b' }}>Branch: {item.branch}</span>
                                      </div>
                                      <div style={{ display: 'flex', gap: 8 }}>
                                        <button
                                          onClick={() => startEditingEmail(item.email, item.branch)}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#2563eb',
                                            cursor: 'pointer',
                                            fontSize: 13,
                                            fontWeight: 500
                                          }}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          onClick={() => handleDeleteEmail(item.email, item.branch)}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#ef4444',
                                            cursor: 'pointer',
                                            fontSize: 13,
                                            fontWeight: 500
                                          }}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </section>

              <section className="info-personal-group">
                <div className="info-personal-group-header">
                  <h3 style={sectionBlockTitleStyle}>Branding</h3>
                  <p style={sectionBlockDescriptionStyle}>Company logo and visual identity</p>
                </div>

                {/* Logo Upload */}
                <div className="info-personal-block">
                  <h3 style={sectionBlockTitleStyle}><FieldHelp id="info.companyLogo">Company Logo</FieldHelp></h3>
                  {logoLoading ? (
                    <div style={{ textAlign: "center", padding: "40px 20px" }}>
                      <p style={{ color: "#64748b" }}>Loading logo...</p>
                    </div>
                  ) : (
                    <>
                      <div style={logoUploadWrapperStyle}>
                        {currentLogoDisplay ? (
                          <div style={logoDisplayWrapperStyle}>
                            <img
                              src={currentLogoDisplay}
                              alt="Company logo"
                              style={logoDisplayStyle}
                            />
                            <button
                              type="button"
                              style={editLogoButtonStyle}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              Update Logo
                            </button>
                          </div>
                        ) : (
                          <div
                            style={logoDropAreaStyle}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <span>Click to upload logo</span>
                          </div>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/jpg"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleLogoSelect(file);
                          }}
                        />
                      </div>
                      <div style={{ marginTop: 12, textAlign: "right" }}>
                        <button
                          type="button"
                          style={{
                            ...primaryButtonStyle,
                            opacity: logoDirty ? 1 : 0.5,
                          }}
                          disabled={!logoDirty}
                          onClick={handleLogoSubmit}
                        >
                          {logoExists || currentLogoDisplay
                            ? "Save Updated Logo"
                            : "Upload Logo"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </section>
            </div>
          </div>
        );

      case "machines": {
        // ── DEBUG: remove after confirming filter works ──────────────────
        console.log("[Branch Filter] selectedBranch:", selectedBranch);
        console.log("[Branch Filter] machineToBranchMap:", machineToBranchMap);
        console.log("[Branch Filter] meterIdToBranchMap:", meterIdToBranchMap);
        if (machines.length > 0) {
          console.log("[Branch Filter] sample machine:", machines[0]);
        }
        // ────────────────────────────────────────────────────────────────

        // Resolve branch for a machine using name first, then meter_id as fallback
        const getMachineBranch = (m) =>
          machineToBranchMap[m.machine_name] ||
          meterIdToBranchMap[String(m.meter_id)] ||
          null;

        const filteredMachines =
          selectedBranch === "All"
            ? machines
            : machines.filter(
              (m) =>
                // Include the branch node card itself (e.g. "lahore Branch" red-dot header)
                m.machine_name === selectedBranch ||
                getMachineBranch(m) === selectedBranch
            );
        return (
          <div className="info-content-card">
            <header style={contentHeaderStyle}>
              <div>
                <h2 style={contentTitleStyle}>Node Settings</h2>
                <p style={contentSubtitleStyle}>
                  Manage node configurations
                  {selectedBranch !== "All" && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: "#1BA08E", fontWeight: 600 }}>
                      — {selectedBranch}
                    </span>
                  )}
                </p>
              </div>
              {!machineLoading && filteredMachines.length > 0 && (
                <button
                  type="button"
                  onClick={() => setExpandAll((prev) => !prev)}
                  style={{
                    background: "none",
                    border: "1px solid #dbe4f0",
                    borderRadius: 8,
                    padding: "8px 16px",
                    fontSize: 13,
                    color: "#334155",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  {expandAll ? "Collapse All" : "Expand All"}
                </button>
              )}
            </header>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {machineLoading && <p>Loading machines...</p>}
              {!machineLoading && machines.length === 0 && (
                <p>No machines found.</p>
              )}
              {!machineLoading &&
                machines.length > 0 &&
                filteredMachines.length === 0 && (
                  <p style={{ color: "#64748b", fontSize: 14 }}>
                    No machines found for branch:{" "}
                    <strong>{selectedBranch}</strong>.
                  </p>
                )}
              {filteredMachines.map((machine) => (
                <MachineCard
                  key={machine.machine_id}
                  machine={machine}
                  onUpdate={handleMachineUpdate}
                  forceExpand={expandAll}
                  clientEmails={clientEmailSuggestions}
                  branchName={getMachineBranch(machine)}
                  showBranch={true}
                  defaultColor={machineColorMap[machine.machine_name]}
                  focusField={
                    matchesFocusTarget(machine, focusTarget)
                      ? focusTarget.focus || FOCUS_RATED_POWER
                      : null
                  }
                />
              ))}
            </div>
          </div>
        );
      }

      case "notifications":
        return (
          <div className="info-content-card">
            <header style={contentHeaderStyle}>
              <h2 style={contentTitleStyle}>Notifications</h2>
            </header>

            {/* Energy (Daily) Report Schedule — grouped by branch (daily_report_email) */}
            {hasEnergyMeter && (
              <>
                <div style={dividerStyle} />
                <div className="info-section-group">
                  <h3 style={sectionBlockTitleStyle}><FieldHelp id="info.energyReportSchedule">Email Notification</FieldHelp></h3>
                  <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
                    Email the Energy Report per source (or Main / whole system). For
                    each source pick its recipients, the report period (Daily / Weekly
                    / 15 Days / Monthly), and one or more time windows. Times are
                    24-hour (HH:MM).
                  </p>

                  <BranchScheduleEditor
                    value={reportSchedule}
                    onChange={setReportSchedule}
                    onSave={handleSaveReportSchedule}
                    saving={reportSaving}
                    loading={reportLoading}
                    branches={branches}
                    branchMeta={branchMeta}
                    branchesLoading={branchesLoading}
                    styles={{ inputGroupStyle, textInputStyle, primaryButtonStyle, actionButtonStyle }}
                    emptyText="No sources configured yet. Click “Add Source” to create one."
                    onInvalidEmail={() => showToast("Invalid email format", "error")}
                  />
                </div>
              </>
            )}

            {/* Energy Efficiency Report Schedule — same grouped shape as the daily report */}
            {hasEnergyMeter && (
              <>
                <div style={dividerStyle} />
                <div className="info-section-group">
                  <h3 style={sectionBlockTitleStyle}><FieldHelp id="info.efficiencyReportSchedule">Energy Efficiency Report Schedule</FieldHelp></h3>
                  <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
                    Email the Energy Efficiency Report per source (or Main / whole
                    system). For each source pick its recipients, the report period
                    (Daily / Weekly / 15 Days / Monthly), and one or more time
                    windows. Times are 24-hour (HH:MM).
                  </p>

                  <BranchScheduleEditor
                    value={effSchedule}
                    onChange={setEffSchedule}
                    onSave={handleSaveEffSchedule}
                    saving={effSaving}
                    loading={effLoading}
                    branches={branches}
                    branchMeta={branchMeta}
                    branchesLoading={branchesLoading}
                    styles={{ inputGroupStyle, textInputStyle, primaryButtonStyle, actionButtonStyle }}
                    emptyText="No schedules yet. Click “Add Source” to create one."
                    onInvalidEmail={() => showToast("Invalid email format", "error")}
                  />
                </div>
              </>
            )}

            {/* Environment Report Schedule (environment_report_email) */}
            {hasEnvironmentMeter && (
              <>
                <div style={dividerStyle} />
                <div className="info-section-group">
                  <h3 style={sectionBlockTitleStyle}>Environment Report Schedule</h3>
                  <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
                    Email periodic temperature and humidity summary reports per environment sensor (or for all sensors across the whole facility).
                    For each sensor group, configure recipients, report period (Daily / Weekly / 15 Days / Monthly), and one or more time windows. Times are 24-hour (HH:MM).
                  </p>

                  <EnvironmentScheduleEditor
                    value={envSchedule}
                    onChange={setEnvSchedule}
                    onSave={handleSaveEnvSchedule}
                    saving={envSaving}
                    loading={envLoading}
                    sensors={envSensors}
                    sensorsLoading={envSensorsLoading}
                    styles={{ inputGroupStyle, textInputStyle, primaryButtonStyle, actionButtonStyle }}
                    emptyText="No environment schedules configured yet. Click “Add Sensor Schedule” to create one."
                    onInvalidEmail={() => showToast("Invalid email format", "error")}
                  />
                </div>
              </>
            )}

            {/* Alert Threshold Settings */}
            {hasEnergyMeter && (
              <>
                <div style={dividerStyle} />
                <div className="info-section-group">
                  <h3 style={sectionBlockTitleStyle}>Alert Thresholds</h3>
                  <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
                    Enable or disable background alert checks for sanctioned load and minimum threshold.
                  </p>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>

                    {/* Sanctioned Load Card */}
                    <div style={thresholdCardStyle}>
                      <p style={thresholdCardTitleStyle}><FieldHelp id="info.sanctionedLoadAlert">Sanctioned Load</FieldHelp></p>
                      <p style={thresholdUnitStyle}>kW</p>
                      <input
                        style={textInputStyle}
                        type="number"
                        value={profileSettings.sanctioned_load ?? ""}
                        readOnly
                      />
                      <button
                        style={{
                          ...togglePillStyle,
                          backgroundColor: profileSettings.sanctioned_load_on ? "#1BA08E" : "#d9d9d9",
                          color: profileSettings.sanctioned_load_on ? "#fff" : "#555",
                        }}
                        disabled={togglingField === "sanctioned_load_on"}
                        onClick={() => handleToggleSetting("sanctioned_load_on")}
                      >
                        {profileSettings.sanctioned_load_on ? "Enabled" : "Disabled"}
                      </button>
                    </div>

                    {/* Minimum Threshold Card */}
                    <div style={thresholdCardStyle}>
                      <p style={thresholdCardTitleStyle}><FieldHelp id="info.minimumThreshold">Minimum Threshold</FieldHelp></p>
                      <p style={thresholdUnitStyle}>kW</p>
                      <input
                        style={textInputStyle}
                        type="number"
                        value={localThreshold}
                        onChange={(e) => setLocalThreshold(e.target.value)}
                        onBlur={async () => {
                          const val = parseFloat(localThreshold);
                          if (!isNaN(val)) {
                            try {
                              await updateClientProfileSettings(username, { minimum_threshold: val });
                              setProfileSettings(prev => ({ ...prev, minimum_threshold: val }));
                              showToast("Threshold value saved", "success");
                            } catch (e) {
                              showToast("Failed to save threshold", "error");
                            }
                          }
                        }}
                      />
                      <button
                        style={{
                          ...togglePillStyle,
                          backgroundColor: profileSettings.minimum_threshold_on ? "#1BA08E" : "#d9d9d9",
                          color: profileSettings.minimum_threshold_on ? "#fff" : "#555",
                        }}
                        disabled={togglingField === "minimum_threshold_on"}
                        onClick={() => handleToggleSetting("minimum_threshold_on", {
                          minimum_threshold: parseFloat(localThreshold) || profileSettings.minimum_threshold,
                        })}
                      >
                        {profileSettings.minimum_threshold_on ? "Enabled" : "Disabled"}
                      </button>
                    </div>

                  </div>
                </div>
              </>
            )}

            {!hasEnergyMeter && !hasEnvironmentMeter && (
              <div style={{ padding: "40px 16px", textAlign: "center", color: "#64748b" }}>
                No notification settings available for your configured meters.
              </div>
            )}
          </div>
        );

      case "multiTier":
        if (!isRootUser) return null;
        return (
          <div className="info-content-card">
            <header style={contentHeaderStyle}>
              <div>
                <h2 style={contentTitleStyle}>Add Credentials</h2>
                <p style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: 14 }}>
                  Create user credentials for specific machines
                </p>
              </div>
            </header>
            <div style={dividerStyle} />

            <div className="info-section-card">
              <div style={formGridStyle}>
                <label style={inputGroupStyle}>
                  <FieldHelp id="info.mtUsername">Username</FieldHelp>
                  <input
                    type="text"
                    value={multiTierForm.username}
                    onChange={(e) => setMultiTierForm({ ...multiTierForm, username: e.target.value })}
                    style={textInputStyle}
                    placeholder="Enter username"
                  />
                </label>
                <label style={inputGroupStyle}>
                  <FieldHelp id="info.mtPassword">Password</FieldHelp>
                  <input
                    type="password"
                    value={multiTierForm.password}
                    onChange={(e) => setMultiTierForm({ ...multiTierForm, password: e.target.value })}
                    style={textInputStyle}
                    placeholder="Enter password"
                  />
                </label>
                <div style={{ ...inputGroupStyle, position: 'relative' }}>
                  <label style={{ fontSize: 14, color: "#0f172a", marginBottom: 6 }}><FieldHelp id="info.primaryNode">Primary Node</FieldHelp></label>
                  {mtLoading ? (
                    <div style={{ ...textInputStyle, color: '#94a3b8' }}>Loading machines...</div>
                  ) : (
                    <NestedMachineSelector
                      isFormMode={true}
                      hierarchyData={mtMachinesTree}
                      selectedNode={{ name: multiTierForm.machineName, meter_id: multiTierForm.meterId }}
                      onSelect={(node) => {
                        setMultiTierForm({
                          ...multiTierForm,
                          meterId: node.meter_id,
                          machineName: node.name,
                          excludedIds: [] // reset exclusions on new selection
                        });
                        setMtSelectedNodeObj(node.rawNode);
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Exclusion UI appears when a primary node is selected */}
              {mtSelectedNodeObj && (
                <div style={{ marginTop: 16 }}>
                  <ExcludeNodeTree
                    treeNode={mtSelectedNodeObj}
                    excludedIds={multiTierForm.excludedIds}
                    onToggleExclude={(id) => {
                      setMultiTierForm(prev => {
                        const newExcludes = prev.excludedIds.includes(id)
                          ? prev.excludedIds.filter(eId => eId !== id)
                          : [...prev.excludedIds, id];
                        return { ...prev, excludedIds: newExcludes };
                      });
                    }}
                  />
                </div>
              )}

              <div style={{ marginTop: 20, textAlign: "right" }}>
                <button
                  type="button"
                  onClick={handleAddCredentials}
                  style={primaryButtonStyle}
                  disabled={mtLoading}
                >
                  Save Information
                </button>
              </div>
            </div>

            <div className="info-section-card">
              <h3 style={{ ...sectionBlockTitleStyle, marginBottom: 4 }}><FieldHelp id="info.recentUsers">Recently Created Users</FieldHelp></h3>
              {credentialsLoading ? (
                <p style={{ color: "#94a3b8", fontSize: 14 }}>Loading users...</p>
              ) : addedCredentials.length === 0 ? (
                <p style={{ color: "#94a3b8", fontSize: 14 }}>No users exist.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {addedCredentials.map((cred, index) => (
                    <div key={cred.username || index} style={{
                      padding: '12px 16px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 14
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontWeight: 600, color: '#334155' }}>
                          User: {cred.username}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <span style={{ fontSize: 12, color: '#64748b' }}>
                            Password:&nbsp;
                            <span style={{ fontFamily: 'monospace', letterSpacing: visiblePasswords[index] ? 'normal' : '2px' }}>
                              {visiblePasswords[index] ? cred.password : '••••••••'}
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={() => setVisiblePasswords(prev => ({ ...prev, [index]: !prev[index] }))}
                            title={visiblePasswords[index] ? 'Hide password' : 'Show password'}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px 4px',
                              color: '#64748b',
                              fontSize: 14,
                              lineHeight: 1,
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {visiblePasswords[index] ? (
                              /* Eye-off SVG */
                              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                              </svg>
                            ) : (
                              /* Eye SVG */
                              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case "dataApi":
        return (
          <div className="info-content-card">
            <header style={contentHeaderStyle}>
              <div>
                <h2 style={contentTitleStyle}>Data API</h2>
                <p style={contentSubtitleStyle}>
                  Pull your energy data into your own spreadsheets, scripts and reports
                </p>
              </div>
            </header>
            <div style={dividerStyle} />

            <div className="info-section-card">
              <h3 style={{ margin: 0, fontSize: 15, color: "#0f172a" }}>
                <FieldHelp id="info.requestUrl">Your request URL</FieldHelp>
              </h3>
              <p style={{ margin: 0, color: "#94a3b8", fontSize: 13 }}>
                A ready-made example for this account. Add your password where shown,
                then change the dates and mode to suit.
              </p>
              <code
                style={{
                  display: "block",
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: "#f4f7fb",
                  border: "1px solid #dbe4f0",
                  fontSize: 12.5,
                  lineHeight: 1.6,
                  color: "#0f172a",
                  wordBreak: "break-all",
                }}
              >
                {dataApiExampleUrl}
              </code>
              <button
                type="button"
                style={{
                  ...actionButtonStyle,
                  alignSelf: "flex-start",
                  border: "1px solid #dbe4f0",
                  background: "#fff",
                  color: "#1BA08E",
                }}
                onClick={handleCopyDataApiUrl}
              >
                Copy URL
              </button>
            </div>

            <div className="info-section-card" style={{ marginTop: 16 }}>
              <p style={{ margin: 0, color: "#475569", fontSize: 14, lineHeight: 1.6 }}>
                A two-page quick start: the endpoint and its parameters, ready-to-run
                curl and Python examples, how to read the response, every measurement a
                node reports, and what each error means.
              </p>

              <a
                href={DATA_API_MANUAL_PATH}
                download="EnViz-Data-API-Manual.pdf"
                style={{
                  ...primaryButtonStyle,
                  display: "inline-flex",
                  alignItems: "center",
                  alignSelf: "flex-start",
                  gap: 8,
                  textDecoration: "none",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download manual (PDF)
              </a>

              <p style={{ margin: 0, color: "#94a3b8", fontSize: 12 }}>
                PDF, 2 pages. Opens in a new tab if your browser previews PDFs.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="info-tab-page">
      <header className="info-tab-header">
      </header>

      <section className="info-tab-layout">
        <aside className="info-tab-sidebar">
          {availableSections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => setActiveSection(section.id)}
              style={sidebarItemStyle(activeSection === section.id)}
            >
              <span style={{ fontWeight: 600 }}>{section.title}</span>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                {section.subtitle}
              </span>
            </button>
          ))}
        </aside>

        <div className="info-tab-content">{renderSectionContent()}</div>
      </section>

      {/* Toast notification */}
      {toast.show && (
        <div style={toastStyle(toast.type)}>
          <span style={{ marginRight: 8, fontSize: 16 }}>
            {toast.type === "success" ? "\u2713" : "\u2717"}
          </span>
          {toast.message}
        </div>
      )}
    </div>
  );
};

const loadTypeOptions = [
  {
    label: "Generation",
    value: "generation",
    children: [
      { label: "Grid", value: "grid" },
      { label: "Solar", value: "solar" },
      { label: "Generator", value: "generator" },
      { label: "Wind mill", value: "wind_mill" },
    ],
  },
  {
    label: "Load",
    value: "load",
    children: [
      { label: "Motor", value: "motor" },
      { label: "Cooling", value: "cooling" },
      { label: "Heating", value: "heating" },
      { label: "Lighting", value: "lighting" },
      { label: "Electronics", value: "electronics" },
      { label: "Electrolysis", value: "electrolysis" },
    ],
  },
  {
    label: "Storage",
    value: "storage",
    children: [
      { label: "UPS", value: "ups" },
      { label: "BESS", value: "bess" },
    ],
  },
  { label: "Building with Solar", value: "building_with_solar" },
  { label: "Building without Solar", value: "building_without_solar" },
];

const HierarchicalSelector = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Recursively find the label for the current value
  const getLabel = (options, val) => {
    for (const opt of options) {
      if (opt.value === val && !opt.children) return opt.label;
      if (opt.children) {
        const found = getLabel(opt.children, val);
        if (found) return found;
      }
    }
    return val || "Select Load Type";
  };

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <div
        style={{
          ...textInputStyle,
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#fff",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{getLabel(loadTypeOptions, value)}</span>
        <span style={{ fontSize: 10, opacity: 0.6 }}>▼</span>
      </div>

      {isOpen && (
        <div style={dropdownMenuStyle}>
          {loadTypeOptions.map((option) => (
            <DropdownItem
              key={option.label}
              option={option}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DropdownItem = ({ option, onSelect }) => {
  const [isHovered, setIsHovered] = useState(false);

  const hasChildren = option.children && option.children.length > 0;

  const handleClick = (e) => {
    if (!hasChildren) {
      e.stopPropagation();
      onSelect(option.value);
    }
  };

  return (
    <div
      style={{
        ...dropdownItemStyle,
        background: isHovered ? "#f1f5f9" : "#fff",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <span>{option.label}</span>
        {hasChildren && <span style={{ fontSize: 10, opacity: 0.6 }}>▶</span>}
      </div>

      {hasChildren && isHovered && (
        <div style={submenuStyle}>
          {option.children.map((child) => (
            <DropdownItem
              key={child.label}
              option={child}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Helper to derive UCL as 0.1 above Upper amber limit
const computeUclFromUal = (ual) => {
  if (ual === "" || ual === null || ual === undefined) return "";
  const num = Number(ual);
  if (!Number.isFinite(num)) return "";
  return String(Math.round((num + 0.1) * 10) / 10);
};

// Sub-component for Machine Card
const MachineCard = ({ machine, onUpdate, forceExpand, clientEmails = [], branchName = null, showBranch = false, defaultColor = "#64748b", focusField = null }) => {
  const [config, setConfig] = useState({
    load_type: machine.load_type,
    active_alert: machine.active_alert,
    inactive_alert: machine.inactive_alert,
    rated_power_alert: machine.rated_power_alert,
    inactive_threshold_minutes: machine.inactive_threshold_minutes,
    active_threshold_minutes: machine.active_threshold_minutes,
    color: machine.color,
    start_hour: machine.start_hour,
    end_hour: machine.end_hour,
    rated_power: machine.rated_power,
    insight_value: machine.insight_value,
    worth_of_equipment: machine.worth_of_equipment || machine.we || "",
    production_value_per_hour: machine.production_value_per_hour || machine.pv || "",
    maintenance_cost: machine.maintenance_cost || machine.mc || "",
    pr: machine.pr ?? machine.probability_of_replacement ?? "",
    pm: machine.pm ?? machine.probability_of_maintenance ?? "",
    // QCP limits (ENVIRONMENT sensors only). Use ?? so a saved 0 is kept, not blanked.
    qcp_no: machine.qcp_no ?? "",
    standard_temp: machine.standard_temp ?? "",
    ucl_temp:
      machine.ual_temp !== undefined && machine.ual_temp !== null && machine.ual_temp !== ""
        ? computeUclFromUal(machine.ual_temp)
        : (machine.ucl_temp ?? ""),
    lcl_temp: machine.lcl_temp ?? "",
    ual_temp: machine.ual_temp ?? "",
    lal_temp: machine.lal_temp ?? "",
    // Room size in metres (ENVIRONMENT sensors only). Feeds the air-energy
    // figures on Temperature > Efficiency. "" clears it.
    room_length_m: machine.room_length_m ?? "",
    room_width_m: machine.room_width_m ?? "",
    room_height_m: machine.room_height_m ?? "",
  });

  // Temperature sensors have no electrical settings — they get QCP limits instead.
  const isEnvironment =
    String(machine.category || "").toUpperCase() === "ENVIRONMENT";
  // Only offer room size when the API returns the fields, so a backend without
  // them never receives keys it doesn't know.
  const supportsRoomSize = isEnvironment && "room_length_m" in machine;

  // effectiveColor: user-saved color takes priority; fall back to auto-assigned default
  const effectiveColor = (config.color && config.color !== "#000000" && config.color !== "#000") ? config.color : defaultColor;
  const [isDirty, setIsDirty] = useState(false);
  const [qcpError, setQcpError] = useState("");
  const [isExpanded, setIsExpanded] = useState(forceExpand || false);
  const [lossParamError, setLossParamError] = useState("");

  // Per-machine alert email recipients
  const [alertEmails, setAlertEmails] = useState([]);
  const [alertEmailsLoaded, setAlertEmailsLoaded] = useState(false);
  const [alertEmailsLoading, setAlertEmailsLoading] = useState(false);
  const [alertEmailsDirty, setAlertEmailsDirty] = useState(false);
  const [customEmailInput, setCustomEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const anyAlertEnabled =
    config.rated_power_alert || config.active_alert || config.inactive_alert;

  useEffect(() => {
    setIsExpanded(forceExpand);
  }, [forceExpand]);

  // Deep-link landing: open this card, bring it into view and flash the field
  // the caller asked about, so it is obvious what needs filling in.
  const cardRef = useRef(null);
  const [flashField, setFlashField] = useState(null);
  useEffect(() => {
    if (!focusField) return;
    setIsExpanded(true);
    setFlashField(focusField);
    // Wait for the expanded body to lay out before scrolling to it.
    const scrollTimer = setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    const flashTimer = setTimeout(() => setFlashField(null), FOCUS_FLASH_MS);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(flashTimer);
    };
  }, [focusField]);

  // Amber ring on the field the deep link pointed at.
  const flashStyle = (field) =>
    flashField === field
      ? {
        borderColor: "#f59e0b",
        boxShadow: "0 0 0 3px rgba(245, 158, 11, 0.25)",
      }
      : null;

  // Lazy-load saved alert recipients when the card is first expanded
  useEffect(() => {
    if (!isExpanded || alertEmailsLoaded || !machine.meter_id) return;
    setAlertEmailsLoading(true);
    getMachineAlertEmails(machine.meter_id, machine.machine_name)
      .then((emails) => setAlertEmails(emails || []))
      .catch((err) => {
        console.error("Failed to load machine alert emails", err);
        setAlertEmails([]);
      })
      .finally(() => {
        setAlertEmailsLoaded(true);
        setAlertEmailsLoading(false);
      });
  }, [isExpanded, alertEmailsLoaded, machine.meter_id, machine.machine_name]);

  const handleChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
      // UCL is derived as 0.1 above Upper amber limit (UAL + 0.1).
      ...(field === "ual_temp" ? { ucl_temp: computeUclFromUal(value) } : {}),
    }));
    setIsDirty(true);
    setLossParamError("");
    setQcpError("");
  };

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

  const addAlertEmail = (email) => {
    const trimmed = (email || "").trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setEmailError("Invalid email format");
      return;
    }
    if (alertEmails.includes(trimmed)) {
      setEmailError("Already added");
      return;
    }
    setAlertEmails((prev) => [...prev, trimmed]);
    setAlertEmailsDirty(true);
    setEmailError("");
  };

  const removeAlertEmail = (email) => {
    setAlertEmails((prev) => prev.filter((e) => e !== email));
    setAlertEmailsDirty(true);
  };

  const handeSave = async () => {
    // Backend 400s when UCL <= LCL; surface it immediately instead of round-tripping.
    // Negative values and 0 are legitimate (a freezer limit is -23 C), so only
    // compare when both fields actually hold a number.
    if (isEnvironment) {
      // Band must read LCL < Standard <= LAL < UAL, with UCL taken from UAL:
      //
      //   LCL ---------- LAL ---------- UAL = UCL
      //   red  |  green   |    amber    |   red
      //
      // Every limit is independently optional, so only compare pairs where both
      // values were supplied -- a sensor with no amber limits stays green up to
      // UCL. Mirrors validate_qcp_limits() in the API so we fail here, not
      // after a round trip.
      const toLimit = (raw) =>
        raw === "" || raw === null || raw === undefined ? null : Number(raw);
      const ucl = toLimit(config.ucl_temp);
      const lcl = toLimit(config.lcl_temp);
      const ual = toLimit(config.ual_temp);
      const lal = toLimit(config.lal_temp);
      const standard = toLimit(config.standard_temp);

      const nonNumeric = [ucl, lcl, ual, lal, standard].some(
        (v) => v !== null && !Number.isFinite(v),
      );
      if (nonNumeric) {
        setQcpError("Temperature limits must be numbers.");
        return;
      }
      if (ucl !== null && lcl !== null && ucl <= lcl) {
        setQcpError("UCL must be greater than LCL.");
        return;
      }
      if (ual !== null && lal !== null && ual <= lal) {
        setQcpError("Upper amber limit must be greater than lower amber limit.");
        return;
      }
      if (lal !== null && lcl !== null && lal <= lcl) {
        setQcpError("Lower amber limit must be above LCL.");
        return;
      }
      if (ual !== null && lcl !== null && ual <= lcl) {
        setQcpError("Upper amber limit must be above LCL.");
        return;
      }
      // The target has to sit in the green band, which ends at the lower amber
      // limit -- otherwise a room reads Amber at the very temperature it is
      // meant to hold.
      if (standard !== null) {
        if (lcl !== null && standard < lcl) {
          setQcpError("Standard cannot be below LCL.");
          return;
        }
        const greenTop = lal !== null ? lal : ucl;
        if (greenTop !== null && standard > greenTop) {
          setQcpError(
            lal !== null
              ? "Standard must be at or below the lower amber limit, so the target sits in the green band."
              : "Standard cannot be above UCL.",
          );
          return;
        }
      }

      if (supportsRoomSize) {
        // Volume needs all three sides, and the API rejects anything <= 0.
        const sides = [config.room_length_m, config.room_width_m, config.room_height_m]
          .map(toLimit)
          .filter((v) => v !== null);
        if (sides.some((v) => !Number.isFinite(v) || v <= 0)) {
          setQcpError("Room length, width and height must be greater than 0.");
          return;
        }
        if (sides.length > 0 && sides.length < 3) {
          setQcpError("Enter room length, width and height, or leave all three empty.");
          return;
        }
      }
    }

    // Validate minimum loss calculation thresholds if provided
    if (config.worth_of_equipment && Number(config.worth_of_equipment) < 500000) {
      setLossParamError("Estimated Worth of Equipment must be at least PKR 500,000");
      return;
    }
    if (config.production_value_per_hour && Number(config.production_value_per_hour) < 1000) {
      setLossParamError("Production Value per Hour must be at least PKR 1,000/hr");
      return;
    }
    if (config.maintenance_cost && Number(config.maintenance_cost) < 1000) {
      setLossParamError("Average Maintenance Cost must be at least PKR 1,000");
      return;
    }
    if (config.pr !== "" && config.pr !== null && config.pr !== undefined) {
      const prVal = Number(config.pr);
      if (isNaN(prVal) || prVal < 0 || prVal > 1) {
        setLossParamError("Probability of replacement must be between 0 and 1");
        return;
      }
    }
    if (config.pm !== "" && config.pm !== null && config.pm !== undefined) {
      const pmVal = Number(config.pm);
      if (isNaN(pmVal) || pmVal < 0 || pmVal > 1) {
        setLossParamError("Probability of maintenance must be between 0 and 1");
        return;
      }
    }

    if (isDirty) {
      const payload = {
        ...config,
        pr: config.pr === "" || config.pr === null || config.pr === undefined ? null : Number(config.pr),
        pm: config.pm === "" || config.pm === null || config.pm === undefined ? null : Number(config.pm),
      };
      if (!supportsRoomSize) {
        delete payload.room_length_m;
        delete payload.room_width_m;
        delete payload.room_height_m;
      }
      onUpdate(machine.machine_id, payload);
      setIsDirty(false);
    }
    if (alertEmailsDirty && machine.meter_id) {
      try {
        await setMachineAlertEmails(
          machine.meter_id,
          machine.machine_name,
          alertEmails,
        );
        setAlertEmailsDirty(false);
      } catch (err) {
        console.error("Failed to save alert emails", err);
      }
    }
  };

  const cardDirty = isDirty || alertEmailsDirty;

  // Summary of active alerts for collapsed view
  const alertSummary = [];
  if (config.rated_power_alert) alertSummary.push("Power");
  if (config.active_alert) alertSummary.push("Outside Hours");
  if (config.inactive_alert) alertSummary.push("Inactive");

  return (
    <div
      ref={cardRef}
      style={{
        backgroundColor: "#fff",
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        border: cardDirty
          ? "1px solid #1BA08E"
          : flashField
            ? "1px solid #f59e0b"
            : "1px solid #e2e8f0",
        overflow: "visible",
      }}
    >
      {/* Collapsed Header - always visible */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          cursor: "pointer",
          background: isExpanded ? "#f8fafc" : "#fff",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: effectiveColor,
            border: "1px solid #e2e8f0",
            flexShrink: 0,
          }} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{machine.machine_name}</h3>
          {showBranch && branchName && (
            <span style={{ fontSize: 11, color: "#1BA08E", background: "#e6f7f5", padding: "2px 8px", borderRadius: 6, fontWeight: 600, letterSpacing: 0.2 }}>
              {branchName}
            </span>
          )}
          {config.rated_power && (
            <span style={{ fontSize: 12, color: "#64748b", background: "#f1f5f9", padding: "2px 8px", borderRadius: 6 }}>
              {config.rated_power} kW
            </span>
          )}
          {alertSummary.length > 0 && (
            <span style={{ fontSize: 12, color: "#1BA08E", background: "#e6f7f5", padding: "2px 8px", borderRadius: 6 }}>
              {alertSummary.join(", ")}
            </span>
          )}
          {cardDirty && (
            <span style={{ fontSize: 11, color: "#f59e0b", fontWeight: 600 }}>Unsaved</span>
          )}
        </div>
        <span style={{ fontSize: 12, color: "#94a3b8", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>
          ▼
        </span>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ padding: "0 20px 20px" }}>

          {/* All settings in one row-based layout */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>

            {/* Left: Machine config */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: "1 1 300px" }}>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
                {!isEnvironment && (
                  <>
                    <label style={{ ...inputGroupStyle, flex: 1, minWidth: "120px" }}>
                      <FieldHelp id="info.ratedPower">Rated Power (kW)</FieldHelp>
                      <input
                        type="number"
                        step="0.1"
                        value={config.rated_power || ""}
                        onChange={(e) => handleChange("rated_power", e.target.value)}
                        style={{ ...textInputStyle, ...flashStyle(FOCUS_RATED_POWER) }}
                        placeholder="e.g. 50.5"
                      />
                    </label>
                    <label style={{ ...inputGroupStyle, flex: 1, minWidth: "150px" }}>
                      <FieldHelp id="info.loadType">Load Type</FieldHelp>
                      <HierarchicalSelector
                        value={config.load_type}
                        onChange={(val) => handleChange("load_type", val)}
                      />
                    </label>
                  </>
                )}

                {isEnvironment && (
                  <>
                    <label style={{ ...inputGroupStyle, flex: 1, minWidth: "110px" }}>
                      <FieldHelp id="info.qcpNo">QCP No</FieldHelp>
                      <input
                        type="text"
                        value={config.qcp_no ?? ""}
                        onChange={(e) => handleChange("qcp_no", e.target.value)}
                        style={textInputStyle}
                        placeholder="e.g. QCP 1"
                      />
                    </label>
                    {/* Target first, then the band cold to warm: LCL | green |
                        lower amber | amber | upper amber (= UCL) | red */}
                    <label style={{ ...inputGroupStyle, flex: 1, minWidth: "110px" }}>
                      <FieldHelp id="info.standardTemp">Standard (°C)</FieldHelp>
                      <input
                        type="number"
                        step="0.1"
                        value={config.standard_temp ?? ""}
                        onChange={(e) => handleChange("standard_temp", e.target.value)}
                        style={textInputStyle}
                        placeholder="e.g. -18"
                      />
                    </label>
                    <label style={{ ...inputGroupStyle, flex: 1, minWidth: "110px" }}>
                      <FieldHelp id="info.lclTemp">LCL (°C)</FieldHelp>
                      <input
                        type="number"
                        step="0.1"
                        value={config.lcl_temp ?? ""}
                        onChange={(e) => handleChange("lcl_temp", e.target.value)}
                        style={textInputStyle}
                        placeholder="e.g. -24"
                      />
                    </label>
                    <label
                      style={{ ...inputGroupStyle, flex: 1, minWidth: "110px" }}
                    >
                      <FieldHelp id="info.lalTemp">Lower amber limit (°C)</FieldHelp>
                      <input
                        type="number"
                        step="0.1"
                        value={config.lal_temp ?? ""}
                        onChange={(e) => handleChange("lal_temp", e.target.value)}
                        style={textInputStyle}
                        placeholder="e.g. -16"
                      />
                    </label>
                    <label
                      style={{ ...inputGroupStyle, flex: 1, minWidth: "110px" }}
                    >
                      <FieldHelp id="info.ualTemp">Upper amber limit (°C)</FieldHelp>
                      <input
                        type="number"
                        step="0.1"
                        value={config.ual_temp ?? ""}
                        onChange={(e) => handleChange("ual_temp", e.target.value)}
                        style={textInputStyle}
                        placeholder="e.g. -12"
                      />
                    </label>
                    {/* Read-only: UCL is derived as 0.1 above Upper amber limit */}
                    <label style={{ ...inputGroupStyle, flex: 1, minWidth: "110px" }}>
                      <FieldHelp id="info.uclTemp">UCL (°C)</FieldHelp>
                      <input
                        type="number"
                        step="0.1"
                        value={config.ucl_temp ?? ""}
                        readOnly
                        disabled
                        tabIndex={-1}
                        title="Derived as Upper amber limit + 0.1"
                        style={{
                          ...textInputStyle,
                          background: "#f1f5f9",
                          color: "#64748b",
                          cursor: "not-allowed",
                        }}
                        placeholder="from upper amber + 0.1"
                      />
                    </label>
                  </>
                )}
                <label style={{ ...inputGroupStyle, flex: "0 0 auto" }}>
                  <FieldHelp id="info.color">Color</FieldHelp>
                  <input
                    type="color"
                    value={effectiveColor.startsWith("hsl") ? "#64748b" : effectiveColor}
                    onChange={(e) => handleChange("color", e.target.value)}
                    style={{ padding: 0, height: 38, width: 44, borderRadius: 8, border: "1px solid #dbe4f0", cursor: "pointer" }}
                  />
                </label>
              </div>

              {/* Room size, for the air energy figures on Temperature > Efficiency */}
              {supportsRoomSize && (
                <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
                  <label style={{ ...inputGroupStyle, flex: 1, minWidth: "110px" }}>
                    Length (m)
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={config.room_length_m ?? ""}
                      onChange={(e) => handleChange("room_length_m", e.target.value)}
                      style={textInputStyle}
                      placeholder="e.g. 10"
                    />
                  </label>
                  <label style={{ ...inputGroupStyle, flex: 1, minWidth: "110px" }}>
                    Width (m)
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={config.room_width_m ?? ""}
                      onChange={(e) => handleChange("room_width_m", e.target.value)}
                      style={textInputStyle}
                      placeholder="e.g. 8"
                    />
                  </label>
                  <label style={{ ...inputGroupStyle, flex: 1, minWidth: "110px" }}>
                    Height (m)
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={config.room_height_m ?? ""}
                      onChange={(e) => handleChange("room_height_m", e.target.value)}
                      style={textInputStyle}
                      placeholder="e.g. 3"
                    />
                  </label>
                </div>
              )}

              {isEnvironment && qcpError && (
                <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 500 }}>
                  {qcpError}
                </div>
              )}
            </div>

            {/* Right: Alerts — electrical nodes only */}
            {!isEnvironment && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: "1 1 300px", justifyContent: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={config.rated_power_alert || false}
                    onChange={(e) => handleChange("rated_power_alert", e.target.checked)}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 500, color: "#334155" }}><FieldHelp id="info.ratedPowerAlert">Alert if rated power exceeded</FieldHelp></span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={config.active_alert || false}
                    onChange={(e) => handleChange("active_alert", e.target.checked)}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 500, color: "#334155" }}><FieldHelp id="info.activeOutsideHoursAlert">Alert if active outside hours</FieldHelp></span>
                  {config.active_alert && (
                    <>
                      <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>after</span>
                      <input
                        type="number"
                        min="1"
                        value={config.active_threshold_minutes || ""}
                        onChange={(e) => handleChange("active_threshold_minutes", parseInt(e.target.value) || "")}
                        style={{ ...textInputStyle, width: 55, padding: "4px 8px", textAlign: "center" }}
                        placeholder="15"
                      />
                      <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>min</span>
                    </>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={config.inactive_alert || false}
                    onChange={(e) => handleChange("inactive_alert", e.target.checked)}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 500, color: "#334155" }}><FieldHelp id="info.inactiveDuringHoursAlert">Alert if inactive during hours</FieldHelp></span>
                  {config.inactive_alert && (
                    <>
                      <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>after</span>
                      <input
                        type="number"
                        min="1"
                        value={config.inactive_threshold_minutes || ""}
                        onChange={(e) => handleChange("inactive_threshold_minutes", parseInt(e.target.value) || "")}
                        style={{ ...textInputStyle, width: 55, padding: "4px 8px", textAlign: "center" }}
                        placeholder="15"
                      />
                      <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>min</span>
                    </>
                  )}
                </div>
                {(config.active_alert || config.inactive_alert) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}><FieldHelp id="info.workingHours">Working Hours</FieldHelp></span>
                    <select
                      value={config.start_hour || ""}
                      onChange={(e) => handleChange("start_hour", e.target.value)}
                      style={{ ...textInputStyle, width: 100, padding: "8px 10px" }}
                    >
                      <option value="">Start</option>
                      {Array.from({ length: 24 }, (_, i) => {
                        const h = i.toString().padStart(2, "0") + ":00";
                        return <option key={h} value={h}>{h}</option>;
                      })}
                    </select>
                    <span style={{ fontSize: 14, color: "#64748b" }}>to</span>
                    <select
                      value={config.end_hour || ""}
                      onChange={(e) => handleChange("end_hour", e.target.value)}
                      style={{ ...textInputStyle, width: 100, padding: "8px 10px" }}
                    >
                      <option value="">End</option>
                      {Array.from({ length: 24 }, (_, i) => {
                        const h = i.toString().padStart(2, "0") + ":00";
                        return <option key={h} value={h}>{h}</option>;
                      })}
                    </select>
                  </div>
                )}
              </div>

            )}
          </div>

          {/* Alert Email Recipients (per-machine) */}
          {!isEnvironment && anyAlertEnabled && (
            <div
              style={{
                marginTop: 12,
                padding: "12px 14px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FieldHelp id="info.alertEmails">Alert Emails</FieldHelp>
                <span style={{ fontWeight: 400, color: "#94a3b8" }}>
                  (optional)
                </span>
                <FieldHelp id="info.alertEmails">
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: "1px solid #cbd5e1",
                      color: "#64748b",
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontStyle: "italic",
                      fontFamily: "serif",
                    }}
                    aria-label="Info: about alert emails"
                  >
                    i
                  </span>
                </FieldHelp>
              </div>

              {alertEmailsLoading ? (
                <div style={{ fontSize: 13, color: "#64748b" }}>
                  Loading...
                </div>
              ) : (
                <>
                  {/* Selected emails as chips */}
                  {alertEmails.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginBottom: 8,
                      }}
                    >
                      {alertEmails.map((email) => (
                        <span
                          key={email}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 10px",
                            background: "#e6f7f5",
                            color: "#0f766e",
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          {email}
                          <button
                            type="button"
                            onClick={() => removeAlertEmail(email)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#0f766e",
                              cursor: "pointer",
                              fontSize: 14,
                              lineHeight: 1,
                              padding: 0,
                            }}
                            aria-label={`Remove ${email}`}
                            title="Remove"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Single combobox — type or pick from suggestions */}
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ position: "relative", flex: "1 1 240px", minWidth: 240 }}>
                      <input
                        type="email"
                        value={customEmailInput}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        onChange={(e) => {
                          setCustomEmailInput(e.target.value);
                          if (emailError) setEmailError("");
                          setShowSuggestions(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addAlertEmail(customEmailInput);
                            if (isValidEmail(customEmailInput)) {
                              setCustomEmailInput("");
                            }
                          }
                          if (e.key === "Escape") setShowSuggestions(false);
                        }}
                        placeholder="Type or pick an email"
                        style={{
                          ...textInputStyle,
                          padding: "6px 10px",
                          fontSize: 13,
                          width: "100%",
                          boxSizing: "border-box",
                        }}
                      />
                      {showSuggestions && (() => {
                        const q = customEmailInput.trim().toLowerCase();
                        const filtered = clientEmails
                          .filter((e) => !alertEmails.includes(e))
                          .filter((e) => !q || e.toLowerCase().includes(q));
                        if (filtered.length === 0) return null;
                        return (
                          <ul
                            style={{
                              position: "absolute",
                              top: "calc(100% + 4px)",
                              left: 0,
                              right: 0,
                              margin: 0,
                              padding: 4,
                              listStyle: "none",
                              background: "#fff",
                              border: "1px solid #e2e8f0",
                              borderRadius: 8,
                              boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
                              maxHeight: 180,
                              overflowY: "auto",
                              zIndex: 20,
                            }}
                          >
                            {filtered.map((email) => (
                              <li
                                key={email}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  addAlertEmail(email);
                                  setCustomEmailInput("");
                                  setShowSuggestions(false);
                                }}
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: 6,
                                  fontSize: 13,
                                  color: "#0f172a",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                              >
                                {email}
                              </li>
                            ))}
                          </ul>
                        );
                      })()}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        addAlertEmail(customEmailInput);
                        if (isValidEmail(customEmailInput)) {
                          setCustomEmailInput("");
                        }
                      }}
                      style={{
                        ...primaryButtonStyle,
                        padding: "6px 14px",
                        fontSize: 13,
                      }}
                    >
                      Add
                    </button>
                  </div>

                  {emailError && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#dc2626",
                        marginTop: 6,
                      }}
                    >
                      {emailError}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Loss Calculation Parameters — electrical nodes only */}
          {!isEnvironment && (
            <div
              style={{
                marginTop: 12,
                padding: "14px 16px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FieldHelp id="info.lossParams">Loss Calculation Parameters</FieldHelp>
                <FieldHelp id="info.lossParams">
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: "1px solid #cbd5e1",
                      color: "#64748b",
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontStyle: "italic",
                      fontFamily: "serif",
                    }}
                    aria-label="Info: about loss calculation parameters"
                  >
                    i
                  </span>
                </FieldHelp>
              </div>

              <div className="loss-params-grid">
                <label style={inputGroupStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, minHeight: 18, color: "#334155", fontWeight: 500 }}>
                    <span><FieldHelp id="info.worthOfEquipment">Worth of Equipment</FieldHelp></span>
                  </div>
                  <input
                    type="number"
                    min="500000"
                    step="10000"
                    value={config.worth_of_equipment || ""}
                    onChange={(e) => handleChange("worth_of_equipment", e.target.value)}
                    style={{ ...textInputStyle, ...flashStyle(FOCUS_LOSS_PARAMS) }}
                    placeholder="5,000,000 (Default)"
                  />
                </label>

                <label style={inputGroupStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, minHeight: 18, color: "#334155", fontWeight: 500 }}>
                    <span><FieldHelp id="info.productionValue">Production Value</FieldHelp></span>
                  </div>
                  <input
                    type="number"
                    min="1000"
                    step="100"
                    value={config.production_value_per_hour || ""}
                    onChange={(e) => handleChange("production_value_per_hour", e.target.value)}
                    style={{ ...textInputStyle, ...flashStyle(FOCUS_LOSS_PARAMS) }}
                    placeholder="5,000 (Default)"
                  />
                </label>

                <label style={inputGroupStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, minHeight: 18, color: "#334155", fontWeight: 500 }}>
                    <span><FieldHelp id="info.maintenanceCost">Maintenance Cost</FieldHelp></span>
                  </div>
                  <input
                    type="number"
                    min="1000"
                    step="100"
                    value={config.maintenance_cost || ""}
                    onChange={(e) => handleChange("maintenance_cost", e.target.value)}
                    style={{ ...textInputStyle, ...flashStyle(FOCUS_LOSS_PARAMS) }}
                    placeholder="5,000 (Default)"
                  />
                </label>

                <label style={inputGroupStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, minHeight: 18, color: "#334155", fontWeight: 500 }}>
                    <span><FieldHelp id="info.probReplacement">Probability of replacement</FieldHelp></span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={config.pr ?? ""}
                    onChange={(e) => handleChange("pr", e.target.value)}
                    style={textInputStyle}
                    placeholder="0.20 (Default)"
                  />
                </label>

                <label style={inputGroupStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, minHeight: 18, color: "#334155", fontWeight: 500 }}>
                    <span><FieldHelp id="info.probMaintenance">Probability of maintenance</FieldHelp></span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={config.pm ?? ""}
                    onChange={(e) => handleChange("pm", e.target.value)}
                    style={textInputStyle}
                    placeholder="0.20 (Default)"
                  />
                </label>
              </div>

              {lossParamError && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#dc2626",
                    marginTop: 8,
                    fontWeight: 500,
                  }}
                >
                  {lossParamError}
                </div>
              )}
            </div>
          )}

          <div style={{ textAlign: "right", marginTop: 12 }}>
            <button
              type="button"
              onClick={handeSave}
              disabled={!cardDirty}
              style={{ ...primaryButtonStyle, opacity: cardDirty ? 1 : 0.5, padding: "8px 16px" }}
            >
              Save
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

// Styles
const sidebarItemStyle = (active) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: "12px 16px",
  borderRadius: 12,
  border: active ? "1px solid #2563eb" : "1px solid #e2e8f0",
  background: active ? "#e0ecff" : "#fff",
  cursor: "pointer",
  gap: 4,
});

const contentHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 16,
};

const contentTitleStyle = {
  margin: 0,
};

const contentSubtitleStyle = {
  margin: "4px 0 0",
  color: "#94a3b8",
};

const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 16,
  overflow: "visible", // Changed to prevent clipping of dropdowns
};

const inputGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  fontSize: 14,
  color: "#0f172a",
};

const textInputStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #dbe4f0",
  fontSize: 14,
};

const primaryButtonStyle = {
  padding: "10px 18px",
  borderRadius: 12,
  border: "none",
  background: "linear-gradient(135deg, #1BA08E 60%, #17a98b 100%)",
  color: "white",
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 10px 20px rgba(27,160,142,0.15)",
  transition: "background 0.2s",
};

const actionButtonStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "6px 10px",
  fontSize: 14,
  fontWeight: 500,
  borderRadius: 8,
  transition: "background 0.2s",
};

const sectionBlockTitleStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: "#334155",
};

const sectionBlockDescriptionStyle = {
  margin: "4px 0 0",
  fontSize: 13,
  color: "#64748b",
};

const dividerStyle = {
  height: 0,
  background: "transparent",
  border: "none",
  margin: 0,
};

const toastStyle = (type) => ({
  position: "fixed",
  bottom: 24,
  right: 24,
  padding: "14px 24px",
  borderRadius: 12,
  background: type === "success" ? "#065f46" : "#991b1b",
  color: "#fff",
  fontSize: 14,
  fontWeight: 500,
  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  animation: "slideIn 0.3s ease-out",
});

const toggleRowStyle = {
  display: "flex",
  gap: 12,
  padding: "12px 16px",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
};

const toggleHelpTextStyle = {
  margin: 0,
  color: "#94a3b8",
  fontSize: 13,
};

const logoUploadWrapperStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const logoDisplayWrapperStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 12,
  padding: 24,
  borderRadius: 16,
  background: "#f8fafc",
  border: "1px solid #dbe4f0",
};

const logoDisplayStyle = {
  maxWidth: "100%",
  maxHeight: 200,
  objectFit: "contain",
  borderRadius: 8,
};

const editLogoButtonStyle = {
  padding: "8px 16px",
  borderRadius: 8,
  border: "1px solid #dbe4f0",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 500,
  cursor: "pointer",
  fontSize: 14,
  transition: "all 0.2s ease",
};

const logoDropAreaStyle = {
  border: "2px dashed #cbd5f5",
  borderRadius: 16,
  padding: 24,
  textAlign: "center",
  background: "#f8fafc",
  color: "#94a3b8",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 160,
};

const dropdownMenuStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  width: "100%",
  minWidth: 160,
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  boxShadow:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  zIndex: 50,
  marginTop: 4,
  padding: "4px 0",
};

const dropdownItemStyle = {
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: 14,
  color: "#334155",
  position: "relative",
  userSelect: "none",
};

const submenuStyle = {
  position: "absolute",
  top: 0,
  left: "100%",
  width: "100%",
  minWidth: 160,
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  marginLeft: 4,
  padding: "4px 0",
};

const thresholdCardStyle = {
  flex: "1 1 220px",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
  padding: "16px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const thresholdCardTitleStyle = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: "#334155",
};

const thresholdUnitStyle = {
  margin: 0,
  fontSize: 12,
  color: "#1BA08E",
  fontWeight: 600,
};

const togglePillStyle = {
  alignSelf: "flex-end",
  padding: "6px 20px",
  fontSize: 13,
  fontWeight: 600,
  border: "none",
  borderRadius: 20,
  cursor: "pointer",
  transition: "background-color 0.2s",
};

export default InfoTab;
